/**
 * `<coin-moebius-buy>` — drop-in custom element that turns a static site into
 * a working checkout in two lines: one `<script>` tag for the registration
 * import, one element with the required attributes.
 *
 *     <coin-moebius-buy
 *       project-id="proj_abcd1234"
 *       product-id="goldback-bundle"
 *       amount="59.99"
 *       currency="USD">
 *       Buy now — $59.99
 *     </coin-moebius-buy>
 *
 * The element fetches the project's enabled providers from
 * `/api/projects/:id/public-info` on first click (not on mount — pages that
 * never get clicked don't pay the request). With two or more providers (or an
 * editable amount) it shows a modal with one button per provider; with exactly
 * one provider and a fixed price it skips the redundant one-option picker and
 * enters that provider's flow directly — redirect rails hand off without any
 * popup, while multi-step flows (pay-by-mail, in-page crypto, delivery-email
 * collection) open the popup at their own first real step. Picking a provider
 * (directly or from the picker):
 *
 *   - **stripe / nowpayments / paypal / square / authorizenet** →
 *     `POST /api/checkout/:provider/:projectId`, redirect the buyer to
 *     the returned URL.
 *   - **manual** → same POST, but the response carries a reference code +
 *     mailing instructions which we render inline in the modal.
 *   - **in-page crypto (monero, …)** → same POST, but the response carries an
 *     address + wallet URI + paymentId. We show the address, a QR, and the
 *     amount, then poll `GET /status/:projectId/:paymentId` until it confirms.
 *     The branch is on the response SHAPE, not the provider id, so any coin
 *     returning these fields works the same way.
 *
 * Customization:
 *
 *   - Attribute `label` (or slotted text content) overrides the button text.
 *   - Attribute/property `disabled` — standard button contract. Merchant- or
 *     dev-controlled; independent of the internal busy lock, so a dev-set
 *     disabled survives a full checkout cycle untouched.
 *   - Attribute/property `spinner` — the built-in busy spinner. On by default;
 *     `spinner="false"` (or `el.spinner = false`) turns it off. The spinner
 *     draws in `currentColor` at `1em`, so it inherits the merchant's button
 *     styling instead of imposing ours. Restyle via `::part(spinner)` or
 *     replace it entirely with `<span slot="spinner">…</span>`. Turning the
 *     spinner off never turns off the double-click lock underneath it.
 *   - CSS custom properties: `--cm-color`, `--cm-bg`, `--cm-button-bg`,
 *     `--cm-button-color`, `--cm-button-radius`, `--cm-modal-overlay`,
 *     `--cm-modal-bg`, `--cm-color-danger`, `--cm-color-success`. The status
 *     colors default to shades that meet WCAG AA contrast on the built-in
 *     light/dark surfaces; a merchant overriding them owns that contrast.
 *   - `::part()` selectors: `button`, `modal`, `dialog`, `provider`,
 *     `provider-icon`, `provider-name`, `close`, `instructions`, `spinner`,
 *     `error`. (The "Powered by Coin Moebius" badge deliberately has NO part
 *     and no styling variable: it renders only when the server says so, and
 *     page CSS cannot reach it inside the shadow root.)
 *   - Events (CustomEvent): `cm-busy` (a click began processing; cancel it to
 *     suppress the built-in spinner and drive your own loading UI), `cm-idle`
 *     (processing settled: handoff, popup open, or error), `cm-load-providers`,
 *     `cm-checkout-started`, `cm-error` (cancel it to suppress the built-in
 *     error display and show your own), and `cm-success` (fired when an
 *     in-page crypto payment confirms; detail carries `{ provider, paymentId }`).
 *
 * The element is intentionally framework-agnostic. Beyond a small bundled QR
 * generator it depends on nothing but the browser globals and the public Coin
 * Moebius HTTP API — drop it into any HTML page, Astro / Hugo / 11ty / Jekyll
 * site, or even a raw email-rendered preview (provided the renderer supports
 * custom elements).
 */

import qrcode from 'qrcode-generator';

/** Shape of `/api/projects/:projectId/public-info` returned by whatever endpoint serves the element's `endpoint` attribute. */
export interface PublicProjectInfo {
  projectId: string;
  providers: PublicProviderInfo[];
  /** When true, show the "Powered by Coin Moebius" badge — in the popup, and
   *  under the trigger once the button qualifies for direct checkout. The server
   *  sets it from the merchant's plan + opt-in (always on for Free, default off
   *  for paid). Optional so older/partial responses default to no badge. */
  showBranding?: boolean;
  /** Stock + quantity facts for this button's product. Absent when the button
   *  has no product-id (or the server predates stock). May be ~60s stale (the
   *  endpoint is edge-cached); checkout re-checks authoritatively. */
  product?: {
    /** The product tracks stock and none is left: render the trigger as a
     *  disabled "Sold out" and never open the popup. */
    soldOut: boolean;
    /** Show the quantity stepper in the popup header. */
    quantityEnabled: boolean;
    /** Stepper ceiling (effective stock capped at 99, or 99 untracked). */
    maxQuantity: number | null;
  };
}

/** One enabled provider as advertised by the public-info endpoint. */
export interface PublicProviderInfo {
  id: string;
  /** Coins this rail can charge in, when it carries more than one. */
  coins?: { id: string; label: string }[];
  /** Buyer-facing payment-category label (e.g. "Credit card", "Cryptocurrency"),
   *  supplied by the server — never the gateway brand. */
  name: string;
  /** Per-product custom label set by the merchant. When present, it's shown
   *  in the picker instead of the built-in display name. */
  label?: string;
  publishableKey?: string;
  mailingAddress?: string;
  /** Pay-by-mail instructions to show in PREVIEW mode (the dashboard supplies
   *  the effective text — custom, inherited, or default). Live checkouts get
   *  their instructions from the checkout response instead. */
  humanInstructions?: string;
  goldbackRate?: number;
  /** Days the seller allows a mailed payment to arrive. */
  stockHoldDays?: number;
  /** Effective price for this provider on this product. The merchant can
   *  price a product differently per payment method (for example a fiat
   *  amount by card and a metal-denominated amount by mail), so each
   *  provider can carry its own amount and currency. Absent when the button
   *  has no product-id. */
  price?: { amount: number; currency: string };
  /** When true, collect the buyer's delivery email before handoff. Set by the
   *  server for a cloud-delivered downloadable on a rail whose payment carries no
   *  buyer email (PayPal, Square, Authorize.Net, NOWPayments, Monero), so we ask
   *  the buyer where to send the file. */
  collectEmail?: boolean;
  /** When true, offer an OPTIONAL email before handoff (pay-by-mail): the buyer can enter
   *  one to get a receipt + download when the seller confirms, or skip it. */
  collectEmailOptional?: boolean;
}

/** Shape of `/api/checkout/manual/:projectId` (step 1: preview). */
interface CheckoutManualPreview {
  preview: true;
  mailingAddress: string;
  expectedAmount: number;
  expectedCurrency: string;
  instructions?: string;
}

/** Shape of `/api/checkout/manual/:projectId/confirm` (step 2: committed). */
interface CheckoutManualConfirmed {
  txId: string;
  referenceCode: string;
  mailingAddress: string;
  expectedAmount: number;
  expectedCurrency: string;
  instructions?: string;
  receiptUrl?: string;
}

/**
 * Shape of `/api/checkout/:provider/:projectId` for in-page crypto providers
 * (Monero today; any coin whose checkout returns an address + a wallet URI).
 * Generic on purpose: the popup shows the address, a QR of `uri`, the amount,
 * and polls `/status` — no per-coin code. `xmrAmount` is Monero's decimal
 * amount; future coins may send `amount` + `coin`.
 */
interface CheckoutCryptoResponse {
  paymentId: string;
  address: string;
  uri: string;
  /** Monero's mint names the amount `xmrAmount`. */
  xmrAmount?: number;
  /** Zano's mint names it `assetAmount`, because one rail carries many assets. */
  assetAmount?: number;
  /** Bitcoin's mint names it `btcAmount`. */
  btcAmount?: number;
  bchAmount?: number;
  amount?: number;
  coin?: string;
  /** Zano's mint names the coin `ticker` (ZANO, FUSD, ...). */
  ticker?: string;
  /** The price the worker actually charged, which is the catalog's for a
   *  fixed-price product and not necessarily the `amount` on the page. */
  expectedAmount?: number;
  expectedCurrency?: string;
  expiresAt?: number;
  /** A link the buyer keeps, for when this settles after they close the tab. */
  receiptUrl?: string;
}

interface CryptoProgress {
  state: string;
  confirmations: number | null;
  requiredConfirmations: number | null;
  asset: string | null;
  received: string | null;
  expected: string | null;
}

const BLOCK_MINUTES: Record<string, number> = {
  monero: 2,
  zano: 1,
  bitcoin: 10,
  'bitcoin-cash': 10,
};

const EXPIRED_PROGRESS: CryptoProgress = {
  state: 'expired',
  confirmations: null,
  requiredConfirmations: null,
  asset: null,
  received: null,
  expected: null,
};

/** Normalized, coin-agnostic crypto instructions the modal renders. */
interface CryptoInstructions {
  paymentId: string;
  address: string;
  uri: string;
  displayAmount: number | null;
  coin: string;
  /** Authoritative fiat price, when the rail reported one. */
  expectedAmount?: number;
  expectedCurrency?: string;
  expiresAt?: number;
  /** A link the buyer keeps, for when this settles after they close the tab. */
  receiptUrl?: string;
}

/** Observed attributes — drives `attributeChangedCallback`. */
const OBSERVED_ATTRS = [
  'endpoint',
  'project-id',
  'product-id',
  'amount',
  'currency',
  'label',
  'customer-ref',
  'editable-amount',
  'disabled',
  'spinner',
  'preview',
] as const;

const PRODUCTION_ENDPOINT = 'https://api.coinmoebius.com';
const LOCAL_ENDPOINT = 'http://localhost:8787';
const DEFAULT_LABEL = 'Buy';
// Buyer-facing stock strings. Hardcoded English like every other string in the
// element (it has no i18n layer); the merchant can intercept cm-error to
// localize their own display.
const SOLD_OUT_LABEL = 'Sold out';
const SOLD_OUT_MESSAGE = 'This item is sold out.';
// Matches the worker's MAX_BUYER_QUANTITY; used only when the server response
// enabled the stepper but carried no ceiling.
const MAX_QUANTITY_FALLBACK = 99;

/** Where the "Powered by Coin Moebius" badge links. A dedicated path (not a
 *  query param) so it's countable by page in privacy-respecting analytics. */
const BRANDING_HREF = 'https://www.coinmoebius.com/built-with';
const BRANDING_LABEL = 'Powered by Coin Moebius';

/**
 * In-page crypto status polling. The buyer pays an on-chain address, so unlike a
 * redirect we have to watch for confirmation. We prefer an explicit "Check
 * payment status" button, with this gentle background poll as a backstop, and we
 * stop polling after the timeout so a forgotten tab doesn't poll forever.
 */
const STATUS_POLL_INTERVAL_MS = 15_000;
const STATUS_POLL_TIMEOUT_MS = 60 * 60 * 1000;

/**
 * Which shape of status answer this build understands, sent on every status
 * call so the server can always answer in a shape the asking button can read.
 *
 * Not the package version. It moves only when the answer's shape changes, and
 * a page left open still holds the build it loaded, so the server has to keep
 * honoring older numbers. Sending it is the half that cannot be added later:
 * a button already in someone's browser will never start sending one.
 */
const STATUS_CONTRACT_VERSION = 1;

/**
 * Shadow-DOM stylesheet. Encapsulated from the host page's CSS — the element
 * looks the same regardless of the surrounding stylesheet. Customization is
 * via custom properties and ::part(), declared on the host page's CSS.
 */
const STYLES = `
:host {
	display: inline-block;
	font-family: var(--cm-font, system-ui, -apple-system, BlinkMacSystemFont, sans-serif);
	line-height: 1.5;

	/* Theme = a cohesive set of defaults for the button AND the popup, so the
	   trigger and the dialog it opens read as one neutral surface. Dark is the
	   default; theme="light" swaps the set below. Any explicit --cm-* a merchant
	   sets still wins, since these resolve through it. Neutral charcoal / soft
	   off-white — no absolute black, no stark white. */
	--_cm-fg: var(--cm-color, #ededed);
	--_cm-surface: var(--cm-modal-bg, #2b2b2b);
	--_cm-btn-bg: var(--cm-button-bg, #2b2b2b);
	--_cm-btn-color: var(--cm-button-color, #ededed);
	/* Status colors clear 4.5:1 on the default dark surface (#2b2b2b):
	   #fca5a5 ≈ 7.5:1, #4ade80 ≈ 8:1. theme="light" swaps to darker shades. */
	--_cm-danger: var(--cm-color-danger, #fca5a5);
	--_cm-success: var(--cm-color-success, #4ade80);

	color: var(--_cm-fg);
}

button[part="button"] {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 0.5em;
	padding: 0.65em 1.4em;
	font: inherit;
	font-weight: 600;
	font-size: inherit;
	color: var(--_cm-btn-color);
	background: var(--_cm-btn-bg);
	/* Subtle edge derived from the text color: invisible-ish on a dark button,
	   defining on a light one (so the light-theme button shows on a white page). */
	border: 1px solid color-mix(in srgb, currentColor 14%, transparent);
	border-radius: var(--cm-button-radius, 6px);
	cursor: pointer;
	transition: opacity 120ms ease;
}
button[part="button"]:hover { opacity: 0.9; }
button[part="button"]:disabled { opacity: 0.5; cursor: not-allowed; }

/* Busy = the internal click lock, separate from the public disabled state.
   pointer-events blocks mouse re-clicks; keyboard re-activation is guarded in
   handleClick. The label dims under a centered spinner so the button never
   changes size (no layout shift mid-click). */
button[part="button"] { position: relative; }
button[part="button"][aria-busy="true"] { pointer-events: none; cursor: progress; }
button[part="button"][aria-busy="true"] .cm-label { opacity: 0.35; }
.cm-spinner {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
}
/* The default ring is deliberately brandless: currentColor + em sizing means
   it adopts whatever text color and size the merchant's button already has. */
.cm-spinner-ring {
	width: 1em;
	height: 1em;
	border-radius: 50%;
	border: 0.14em solid currentColor;
	border-top-color: transparent;
	animation: cm-spin 0.8s linear infinite;
}
@keyframes cm-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) {
	.cm-spinner-ring {
		animation: cm-fade 1.2s ease-in-out infinite;
		border-top-color: currentColor;
	}
}
@keyframes cm-fade { 50% { opacity: 0.3; } }

/* Inline error line for the direct (no-popup) path. One quiet line under the
   button: announced as an alert, cleared on the next click, restylable via
   ::part(error) or --cm-color-danger. Defaults to the theme's danger shade
   rather than currentColor so an error can't inherit itself invisible. */
.cm-inline-error {
	margin: 0.4em 0 0;
	font-size: 0.8em;
	line-height: 1.4;
	color: var(--_cm-danger);
}

/* Under-button "Powered by Coin Moebius" credit, shown for plans the server
   flags (always on for Free) once the button qualifies for direct checkout.
   Deliberately NO part and no dedicated custom property: page CSS cannot
   reach inside the shadow root without a door, so for non-developers the
   credit is the price of the free tier. */
.cm-branding--inline {
	display: block;
	margin-top: 0.35em;
	font-size: 0.72em;
	text-align: center;
	color: inherit;
	opacity: 0.55;
	text-decoration: none;
}
.cm-branding--inline:hover { opacity: 0.85; text-decoration: underline; }
.cm-branding--inline:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }

/* Visible keyboard focus on every interactive control (WCAG 2.4.7 / 1.4.11).
   currentColor is the control's own text color, which already contrasts its
   background by design, so the ring stays visible in either theme and on top
   of whatever the merchant themes. Mouse users don't see it (:focus-visible). */
button[part="button"]:focus-visible,
.provider:focus-visible,
.confirm-btn:focus-visible,
.close-btn:focus-visible,
.copy-btn:focus-visible,
.crypto-link:focus-visible,
.amount-input:focus-visible {
	outline: 2px solid currentColor;
	outline-offset: 2px;
}

/* The popup is a native <dialog> opened with showModal(), so the browser
   promotes it to the top layer — above EVERY element on the host page,
   regardless of the page's stacking contexts, z-index ladders, or any
   transformed/filtered ancestor (which would otherwise trap a plain
   position:fixed overlay and let page content paint over the popup). The
   ::backdrop paints the dimming tint; the dialog box itself is a transparent
   full-viewport flex container that centers the inner .dialog. We reset the
   UA dialog chrome (border, padding, max-width/height, centering margins). */
.modal {
	position: fixed;
	inset: 0;
	/* Fill the viewport so the flex centering below applies. Without an explicit
	   width/height the UA width:fit-content wins and the dialog collapses to the
	   corner. border-box keeps the 1rem gutter from overflowing. */
	box-sizing: border-box;
	width: 100%;
	height: 100%;
	max-width: 100%;
	max-height: 100%;
	margin: 0;
	border: 0;
	background: transparent;
	color: inherit;
	overflow: visible;
	padding: 1rem;
	align-items: center;
	justify-content: center;
}
/* display:flex only once open so the UA rule that hides a closed dialog still
   applies before showModal() runs (no full-screen flash). */
.modal[open] { display: flex; }
.modal::backdrop { background: var(--cm-modal-overlay, rgba(0, 0, 0, 0.45)); }

.dialog {
	background: var(--_cm-surface);
	color: var(--_cm-fg);
	border-radius: 16px;
	padding: 1.75rem;
	max-width: 400px;
	width: 100%;
	max-height: calc(100vh - 2rem);
	overflow-y: auto;
	box-shadow: 0 24px 48px -12px rgba(0, 0, 0, 0.25);
}

.dialog__header {
	text-align: center;
	margin-bottom: 1.25rem;
	padding-bottom: 1rem;
	/* Borders/tints derive from the text color so they stay visible on any
	   --cm-modal-bg, in both light and dark mode. */
	border-bottom: 1px solid color-mix(in srgb, currentColor 12%, transparent);
}

.dialog__title {
	font-size: 1rem;
	font-weight: 600;
	margin: 0 0 0.25rem 0;
	letter-spacing: -0.01em;
}

.dialog__amount {
	font-size: 1.75rem;
	font-weight: 700;
	margin: 0;
	letter-spacing: -0.02em;
}

.dialog__amount .currency {
	font-size: 0.875rem;
	font-weight: 500;
	opacity: 0.7;
	margin-inline-start: 0.25rem;
}

/* The Goldback glyph, shown wherever a GBK amount renders. Em-sized and
   currentColor so it tracks the text it replaces the "GBK" code in. */
.gb-symbol {
	width: 1em;
	height: 1em;
	display: inline-block;
	vertical-align: -0.12em;
	flex-shrink: 0;
}

.dialog__amount-edit {
	display: inline-flex;
	align-items: baseline;
	gap: 0.4rem;
	margin-top: 0.25rem;
}

.amount-input {
	width: 7rem;
	font: inherit;
	font-size: 1.6rem;
	font-weight: 700;
	text-align: center;
	letter-spacing: -0.02em;
	color: inherit;
	background: transparent;
	border: none;
	border-bottom: 2px solid color-mix(in srgb, currentColor 22%, transparent);
	padding: 0.1rem 0.25rem;
}
.amount-input:focus {
	border-bottom-color: currentColor;
}

/* Shown only when the entered amount isn't a positive number. Tied to the
   input via aria-describedby; toggled (hidden) in sync with provider gating. */
/* A neutral prompt, not an error: a buyer-priced product just needs an amount
   before they can pick a method. Muted, not red. */
.amount-hint {
	display: block;
	margin-top: 0.4rem;
	font-size: 0.8125rem;
	font-weight: 500;
	color: inherit;
	opacity: 0.7;
	text-align: center;
}

/* Banner over the picker: in preview, a method that would navigate away shows
   this and the payment list stays put. Tinted from the text color so it reads
   in either theme. */
.cm-notice {
	margin: 0 0 0.75rem 0;
	padding: 0.5rem 0.75rem;
	font-size: 0.8125rem;
	font-weight: 500;
	text-align: center;
	border-radius: 8px;
	color: var(--_cm-fg);
	background: color-mix(in srgb, currentColor 8%, transparent);
	border: 1px solid color-mix(in srgb, currentColor 16%, transparent);
}

.amount-input__currency {
	font-size: 0.875rem;
	font-weight: 500;
	opacity: 0.7;
}

/* Quantity stepper: minus / count / plus beside the header amount, shown only
   when the merchant turned the per-product quantity toggle on. */
.dialog__qty {
	display: inline-flex;
	align-items: center;
	gap: 0.75rem;
	margin-top: 0.25rem;
}
.qty-btn {
	width: 2rem;
	height: 2rem;
	font: inherit;
	font-size: 1.1rem;
	font-weight: 700;
	line-height: 1;
	color: inherit;
	background: color-mix(in srgb, currentColor 8%, transparent);
	border: 1px solid color-mix(in srgb, currentColor 16%, transparent);
	border-radius: 8px;
	cursor: pointer;
}
.qty-btn:disabled {
	opacity: 0.35;
	cursor: default;
}
.qty-value {
	min-width: 1.5rem;
	text-align: center;
	font-size: 1.25rem;
	font-weight: 700;
}

.providers {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	margin-bottom: 1rem;
}

.provider {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.6rem;
	padding: 0.8rem 1rem;
	font: inherit;
	font-size: 0.9375rem;
	font-weight: 500;
	color: inherit;
	background: transparent;
	border: 1px solid color-mix(in srgb, currentColor 16%, transparent);
	border-radius: 10px;
	cursor: pointer;
	text-align: center;
	transition: background 100ms ease, border-color 100ms ease;
	width: 100%;
	min-height: 44px;
}
.provider:hover {
	background: color-mix(in srgb, currentColor 6%, transparent);
	border-color: color-mix(in srgb, currentColor 32%, transparent);
}
.provider:disabled { opacity: 0.5; cursor: progress; }

.provider-icon { font-size: 1.125rem; flex-shrink: 0; }
.provider-name { font-weight: 500; }
/* Per-method price, shown only when methods are priced differently. Pushed to
   the trailing edge so the method name stays the focal point; the chip reads as
   a secondary detail. margin-inline-start keeps it correct under RTL. */
.provider-price {
	margin-inline-start: auto;
	padding-inline-start: 0.6rem;
	font-variant-numeric: tabular-nums;
	font-weight: 600;
	opacity: 0.85;
	white-space: nowrap;
}

.confirm-btn {
	display: block;
	width: 100%;
	padding: 0.85rem 1rem;
	font: inherit;
	font-size: 0.9375rem;
	font-weight: 600;
	/* The one CTA inside the popup. Default is inverted (text color on surface
	   color) so it always contrasts the dialog, whatever the theme. A merchant's
	   --cm-button-bg / --cm-button-color still take over when set. */
	color: var(--cm-button-color, var(--_cm-surface));
	background: var(--cm-button-bg, var(--_cm-fg));
	border: 1px solid color-mix(in srgb, currentColor 28%, transparent);
	border-radius: 10px;
	cursor: pointer;
	transition: opacity 100ms ease;
	margin-top: 0.25rem;
	min-height: 44px;
}
.confirm-btn:hover { opacity: 0.9; }
.confirm-btn:disabled { opacity: 0.5; cursor: progress; }

.close-btn {
	display: block;
	width: 100%;
	padding: 0.65rem 1rem;
	margin-top: 0.5rem;
	font: inherit;
	font-size: 0.875rem;
	font-weight: 500;
	color: inherit;
	background: transparent;
	border: 1px solid color-mix(in srgb, currentColor 14%, transparent);
	border-radius: 10px;
	cursor: pointer;
	opacity: 0.7;
	transition: opacity 100ms ease;
	min-height: 44px;
}
.close-btn:hover { opacity: 1; }

/* Secondary "No thanks" on the optional pay-by-mail email screen: ghost style like close. */
.cm-skip-btn {
	display: block;
	width: 100%;
	padding: 0.65rem 1rem;
	margin-top: 0.5rem;
	font: inherit;
	font-size: 0.875rem;
	font-weight: 500;
	color: inherit;
	background: transparent;
	border: 1px solid color-mix(in srgb, currentColor 14%, transparent);
	border-radius: 10px;
	cursor: pointer;
	opacity: 0.7;
	transition: opacity 100ms ease;
	min-height: 44px;
}
.cm-skip-btn:hover { opacity: 1; }

.manual-section {
	margin-bottom: 1rem;
}

.manual-label {
	font-size: 0.75rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	/* 0.65 keeps these small uppercase labels above 4.5:1 in both themes
	   (0.5 dropped the light theme to ~3.2:1). */
	opacity: 0.65;
	margin: 0 0 0.35rem 0;
}

.manual-value {
	font-size: 0.9375rem;
	font-weight: 500;
	margin: 0;
	line-height: 1.4;
}

.manual-address {
	white-space: pre-line;
}

.ref-code {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
	padding: 0.75rem 1rem;
	background: color-mix(in srgb, currentColor 6%, transparent);
	border: 1px dashed color-mix(in srgb, currentColor 22%, transparent);
	border-radius: 8px;
	font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
	font-size: 1.25rem;
	font-weight: 700;
	letter-spacing: 0.08em;
	text-align: center;
	user-select: all;
}

.ref-note {
	font-size: 0.8125rem;
	opacity: 0.7;
	margin: 0;
	text-align: center;
}

.instructions-text {
	font-size: 0.875rem;
	opacity: 0.7;
	margin: 0;
	line-height: 1.5;
	white-space: pre-line;
}

.success-check {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
	font-size: 0.875rem;
	font-weight: 500;
	color: var(--_cm-success);
	margin: 0;
}

.error {
	color: var(--_cm-danger);
	font-size: 0.875rem;
	margin: 0.5rem 0;
	text-align: center;
}

/* Dark is the default. theme="light" swaps the whole neutral set (button +
   popup) to a soft off-white. Borders, tints, and muted text derive from these
   via color-mix / opacity, so a merchant's explicit --cm-* still wins on top.
   No OS detection — the merchant chooses the theme. */
:host([theme='light']) {
	--_cm-fg: var(--cm-color, #1f1f1f);
	--_cm-surface: var(--cm-modal-bg, #f5f5f5);
	--_cm-btn-bg: var(--cm-button-bg, #f5f5f5);
	--_cm-btn-color: var(--cm-button-color, #1f1f1f);
	/* Darker status shades for the light surface (#f5f5f5): #b91c1c ≈ 6:1,
	   #15803d ≈ 4.6:1. The old #16a34a green was only ≈3:1 and failed AA. */
	--_cm-danger: var(--cm-color-danger, #b91c1c);
	--_cm-success: var(--cm-color-success, #15803d);
}

/* In-page crypto: QR + address + status. */
.crypto-qr {
	display: flex;
	justify-content: center;
	margin: 0 0 1rem 0;
}
.crypto-qr svg {
	width: 184px;
	height: 184px;
	/* QR codes need a light quiet zone to scan reliably, so the code sits on
	   white regardless of the popup theme. */
	background: #fff;
	padding: 10px;
	border-radius: 10px;
	box-sizing: border-box;
}
.crypto-amount {
	font-size: 1.05rem;
	font-weight: 600;
	text-align: center;
	margin: 0 0 0.75rem 0;
	font-variant-numeric: tabular-nums;
}
.crypto-headline {
	font-size: 1.15rem;
	font-weight: 700;
	text-align: center;
	margin: 0 0 0.15rem 0;
}
.crypto-sub {
	font-size: 0.85rem;
	text-align: center;
	opacity: 0.7;
	margin: 0 0 0.9rem 0;
}
.crypto-count {
	font-size: 0.95rem;
	text-align: center;
	margin: 0 0 0.2rem 0;
	font-variant-numeric: tabular-nums;
	opacity: 0.75;
}
.crypto-count__done {
	font-size: 1.6rem;
	font-weight: 700;
	opacity: 1;
}
.crypto-eta,
.crypto-reassure {
	font-size: 0.85rem;
	text-align: center;
	opacity: 0.7;
	margin: 0 0 0.75rem 0;
}
.crypto-explain {
	font-size: 0.9rem;
	text-align: center;
	margin: 0 0 0.9rem 0;
	line-height: 1.5;
}
.crypto-figures {
	display: flex;
	flex-direction: column;
	gap: 0.3rem;
	margin-bottom: 0.9rem;
}
.crypto-row {
	display: flex;
	justify-content: space-between;
	gap: 1rem;
	font-size: 0.9rem;
	padding: 0.35rem 0.6rem;
	border-radius: 6px;
	background: color-mix(in srgb, currentColor 6%, transparent);
	font-variant-numeric: tabular-nums;
}
.crypto-row__label {
	opacity: 0.7;
}
.crypto-receipt {
	font-size: 0.8rem;
	text-align: center;
	margin: 0 0 0.75rem 0;
	opacity: 0.75;
	word-break: break-all;
}
.crypto-receipt a {
	color: inherit;
}
.crypto-hold {
	font-size: 0.8rem;
	text-align: center;
	margin: -0.45rem 0 0.75rem 0;
	opacity: 0.7;
	font-variant-numeric: tabular-nums;
}
.crypto-hold--lapsed {
	opacity: 1;
	font-weight: 600;
}
.crypto-address {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.6rem 0.7rem;
	background: color-mix(in srgb, currentColor 6%, transparent);
	border: 1px solid color-mix(in srgb, currentColor 16%, transparent);
	border-radius: 8px;
	margin-bottom: 0.75rem;
}
.crypto-address__value {
	flex: 1;
	min-width: 0;
	font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
	font-size: 0.72rem;
	line-height: 1.4;
	word-break: break-all;
	user-select: all;
}
.copy-btn {
	flex-shrink: 0;
	font: inherit;
	font-size: 0.72rem;
	font-weight: 600;
	padding: 0.3rem 0.55rem;
	color: inherit;
	background: transparent;
	border: 1px solid color-mix(in srgb, currentColor 24%, transparent);
	border-radius: 6px;
	cursor: pointer;
	min-height: 32px;
}
.copy-btn:hover { background: color-mix(in srgb, currentColor 8%, transparent); }
.crypto-link {
	display: block;
	text-align: center;
	font-size: 0.8125rem;
	margin: 0 0 0.75rem 0;
	color: inherit;
	opacity: 0.75;
}
.crypto-status {
	font-size: 0.85rem;
	opacity: 0.8;
	margin: 0.75rem 0 0;
	text-align: center;
	line-height: 1.45;
}
/* Crypto delivery-email step. */
.cm-collect-lead {
	font-size: 0.85rem;
	opacity: 0.8;
	line-height: 1.5;
	margin: 0 0 0.875rem;
}
.cm-email-input {
	width: 100%;
	box-sizing: border-box;
	padding: 0.625rem 0.75rem;
	margin: 0 0 0.75rem;
	font: inherit;
	color: inherit;
	/* A tint of the dialog's own text colour, the way every other inner surface
	   here is built. This used to fall back to white while the text colour was
	   inherited, so on the dark default the field went white and the buyer typed
	   near-white text onto it. Deriving from currentColor cannot drift from the
	   theme, because it IS the theme. */
	background: color-mix(in srgb, currentColor 8%, transparent);
	border: 1px solid color-mix(in srgb, currentColor 30%, transparent);
	border-radius: var(--cm-button-radius, 8px);
}
.cm-email-input::placeholder {
	/* Muted enough to read as a hint, strong enough to actually read. The old
	   placeholder inherited a near-white on white. */
	color: color-mix(in srgb, currentColor 55%, transparent);
	opacity: 1;
}
.cm-email-input:focus-visible {
	outline: 2px solid var(--cm-button-bg, currentColor);
	outline-offset: 1px;
}

/* "Powered by Coin Moebius" badge. Free merchants always show it; paid
   merchants opt in. Muted so it reads as attribution, not a CTA, and never
   competes with the buyer's intent. Color derives from the dialog text so it
   stays legible in either theme. */
.cm-branding {
	display: block;
	margin-top: 0.85rem;
	font-size: 0.75rem;
	text-align: center;
	color: inherit;
	opacity: 0.55;
	text-decoration: none;
	transition: opacity 100ms ease;
}
.cm-branding:hover {
	opacity: 0.85;
	text-decoration: underline;
}
.cm-branding:focus-visible {
	outline: 2px solid currentColor;
	outline-offset: 2px;
}

/* Honor reduced-motion: drop the opacity/color transitions on all controls. */
@media (prefers-reduced-motion: reduce) {
	button[part="button"],
	.provider,
	.confirm-btn,
	.close-btn,
	.copy-btn,
	.cm-branding {
		transition: none;
	}
}
`;

/** Internal modal state — null when closed. */
type ModalState =
  | { kind: 'loading' }
  | { kind: 'picking'; providers: PublicProviderInfo[] }
  // A rail that carries several coins asks which one before minting, because
  // the address and the amount both depend on the answer.
  | { kind: 'picking-coin'; provider: PublicProviderInfo; coins: { id: string; label: string }[] }
  | { kind: 'manual-preview'; payload: CheckoutManualPreview; provider: PublicProviderInfo }
  | {
      kind: 'manual-confirmed';
      payload: CheckoutManualConfirmed;
      provider?: PublicProviderInfo;
    }
  // In-page crypto: show the address + QR + amount and poll for confirmation.
  // `checking` drives the "Checking…" button state; `note` is the status line.
  | {
      kind: 'crypto-pay';
      payload: CryptoInstructions;
      provider: PublicProviderInfo;
      checking: boolean;
      note: string | null;
      /** The coin the buyer chose, when the rail offered a choice. Titles name
       *  this rather than the rail, because "Pay with Zano" over a Freedom
       *  Dollar amount tells the buyer the wrong thing. */
      coinLabel?: string | null;
      progress?: CryptoProgress | null;
    }
  // Carries the instructions it was paid against, so the receipt can name the
  // coin and the amount that actually moved instead of a bare "confirmed".
  | { kind: 'crypto-paid'; provider: PublicProviderInfo; payload?: CryptoInstructions }
  // Delivery email: collected before handoff when the provider asks for it (a
  // cloud-delivered downloadable on a rail whose payment carries no buyer email).
  | { kind: 'collect-email'; provider: PublicProviderInfo; error: string | null; optional: boolean }
  | { kind: 'error'; message: string };

export class CoinMoebiusBuyElement extends HTMLElement {
  static get observedAttributes(): readonly string[] {
    return OBSERVED_ATTRS;
  }

  private modalState: ModalState | null = null;
  /** The coin the buyer picked on a multi-coin rail, or null before they have. */
  private chosenCoin: string | null = null;
  /** That coin's buyer-facing name, for the screens that name what they are
   *  paying in. "Freedom Dollar" reads better than the fUSD ticker. */
  private chosenCoinLabel: string | null = null;
  private cachedProjectInfo: PublicProjectInfo | null = null;
  /**
   * Preview-mode override. When set, the picker renders THIS list instead of
   * fetching `/public-info`, and it's re-read on every open (never cached), so
   * the dashboard can drive a live preview straight from the product form's
   * unsaved state. Ignored outside preview mode. Set as a DOM property
   * (`el.previewProviders = [...]`), not an attribute.
   */
  previewProviders: PublicProviderInfo[] | null = null;
  /**
   * Preview-mode override for the "Powered by Coin Moebius" badge. The dashboard
   * preview never calls `/public-info`, so it can't learn `showBranding`; set
   * this property to mirror what the merchant's buyers will see. Ignored outside
   * preview mode; defaults to off.
   */
  previewBranding = false;
  private fetchImpl: typeof fetch = (...args) => fetch(...args);
  private navigateImpl: (url: string) => void = (url) => {
    window.location.assign(url);
  };
  /**
   * Element that had focus before the modal opened. We restore focus here
   * when the modal closes — standard a11y pattern so keyboard users don't
   * get yanked back to the top of the document.
   */
  private triggerForRestore: HTMLElement | null = null;
  /** Keydown handler bound on modal open; unbound on close. */
  private modalKeydownHandler: EventListener | null = null;
  /**
   * Buyer-entered amount when `editable-amount` is set (tip jars,
   * name-your-price). Seeded from the `amount` attribute as a suggestion
   * when the modal opens, then updated as the buyer types.
   */
  private editableAmount: number | null = null;
  /** True once the buyer has typed in the editable amount field. Until then the
   *  "enter an amount" prompt stays hidden, so a fresh popup never shows it. */
  private amountTouched = false;
  /** In-page crypto status backstop poll; null when not polling. */
  private statusTimer: ReturnType<typeof setInterval> | null = null;
  private holdTimer: ReturnType<typeof setInterval> | null = null;
  private statusPollStartedAt = 0;
  /**
   * Internal click lock: true from the moment a click starts processing until
   * the flow settles (handoff, popup open, or error). Deliberately separate
   * from the public `disabled` property — if the lock wrote to `disabled`,
   * releasing it would stomp a value the developer set on purpose. The button
   * is clickable only when BOTH say so.
   */
  private busyLock = false;
  /** Set when a `cm-busy` listener called preventDefault(): the developer is
   *  drawing their own loading UI, so the built-in spinner stays hidden. The
   *  lock itself is never suppressible. */
  private suppressSpinner = false;
  /** Error message shown inline under the trigger when a direct (no-popup)
   *  checkout fails. Cleared on the next click. */
  private inlineError: string | null = null;
  /** True while the current flow began as a direct (picker-skipped) checkout,
   *  so errors surface inline rather than in a popup that was never open. */
  private directFlow = false;
  /** Latched once the button has qualified for direct checkout (single
   *  provider, fixed price). Drives the under-button branding credit. */
  private directEligible = false;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  /**
   * Standard button contract, usable as an attribute (`disabled`) by
   * non-coders or a property (`el.disabled = true`) by developers and
   * framework bindings. Reflected so CSS and markup stay in sync.
   */
  get disabled(): boolean {
    return this.hasAttribute('disabled') && this.getAttribute('disabled') !== 'false';
  }
  set disabled(value: boolean) {
    if (value) this.setAttribute('disabled', '');
    else this.removeAttribute('disabled');
  }

  /** Built-in busy spinner. Absent attribute = on (the default favors
   *  non-coders); `spinner="false"` or `el.spinner = false` turns it off. */
  get spinner(): boolean {
    return this.getAttribute('spinner') !== 'false';
  }
  set spinner(value: boolean) {
    if (value) this.removeAttribute('spinner');
    else this.setAttribute('spinner', 'false');
  }

  connectedCallback(): void {
    this.render();
  }

  disconnectedCallback(): void {
    // Stop any crypto status poll when the element leaves the DOM so a removed
    // button never keeps a timer alive.
    this.stopStatusPolling();
    this.stopHoldCountdown();
  }

  attributeChangedCallback(): void {
    // Re-render the trigger so attribute changes (e.g., label, amount)
    // reflect immediately. The modal is rendered on demand and reads
    // attributes at click time, so it doesn't need re-rendering here.
    if (this.modalState === null) this.render();
  }

  // --- public API ------------------------------------------------------

  /**
   * Trigger the picker programmatically. Equivalent to a user clicking the
   * default button. Used by consumers who want to wrap the element with
   * their own UI.
   */
  open(): Promise<void> {
    return this.handleClick();
  }

  /**
   * Inject a custom fetch implementation. Primarily for tests — production
   * usage relies on the page's global `fetch`.
   */
  setFetch(fn: typeof fetch): void {
    this.fetchImpl = fn;
  }

  /**
   * Inject a custom navigation implementation. Primarily for tests — production
   * usage redirects via `window.location.assign`.
   */
  setNavigate(fn: (url: string) => void): void {
    this.navigateImpl = fn;
  }

  // --- internals -------------------------------------------------------

  private getRequiredAttr(name: string): string {
    const value = this.getAttribute(name);
    if (!value) {
      throw new Error(`coin-moebius-buy: missing required attribute "${name}"`);
    }
    return value;
  }

  private getValidatedEndpoint(): string {
    const isLocal =
      typeof location !== 'undefined' &&
      (location.hostname === 'localhost' || location.hostname === '127.0.0.1');
    const endpoint =
      this.getAttribute('endpoint') ?? (isLocal ? LOCAL_ENDPOINT : PRODUCTION_ENDPOINT);
    let parsed: URL;
    try {
      parsed = new URL(endpoint);
    } catch {
      throw new Error('coin-moebius-buy: endpoint attribute must be a valid URL');
    }
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
      throw new Error(
        `coin-moebius-buy: endpoint scheme "${parsed.protocol}" is not allowed (expected https: or http:)`,
      );
    }
    return endpoint;
  }

  private async handleClick(): Promise<void> {
    // The double-click guard. pointer-events blocks busy mouse clicks in CSS;
    // this catches keyboard activation and programmatic open() calls. The
    // public `disabled` check backs up the native attribute for the same
    // callers (a disabled native button never fires click, open() ignores it).
    // A sold-out trigger is equally inert.
    if (this.busyLock || this.disabled || this.soldOut) return;
    this.inlineError = null;
    try {
      // Capture the focused element before we rip control away — only on
      // the initial open, not on intra-modal transitions.
      if (this.modalState === null) {
        this.triggerForRestore = (document.activeElement as HTMLElement | null) ?? this;
        // Seed the editable amount from the `amount` attribute (the
        // suggested value) so a buyer who doesn't change it still pays the
        // suggestion, and providers are enabled immediately when there is one.
        if (this.isEditableAmount() && this.editableAmount === null) {
          const suggested = Number.parseFloat(this.getAttribute('amount') ?? '');
          this.editableAmount = Number.isFinite(suggested) && suggested > 0 ? suggested : null;
        }
      }
      // Live preview: the dashboard supplies the provider list from the form's
      // current (unsaved) state. Skip the network and the cache entirely so the
      // popup mirrors the editor each time it's opened — and always show the
      // picker, single provider or not: the preview's whole job is showing it.
      if (this.isPreview() && this.previewProviders) {
        this.modalState = { kind: 'picking', providers: this.previewProviders };
        this.render();
        return;
      }

      // Lock and announce. Canceling cm-busy suppresses only the built-in
      // spinner (the developer draws their own loading UI); the lock holds
      // regardless. The busy state lives on the trigger button, not in a
      // popup, so the direct path never flashes a dialog it won't use.
      this.busyLock = true;
      const busyEvent = this.dispatchCustomEvent('cm-busy', {});
      this.suppressSpinner = busyEvent.defaultPrevented;
      this.render();

      this.dispatchCustomEvent('cm-load-providers');
      const info = await this.loadProjectInfo();

      // Sold out: latch and stop. The trigger re-renders disabled with the
      // "Sold out" label; no popup, no checkout. The signal can be ~60s stale
      // (edge cache) but checkout is the authoritative gate regardless.
      if (info.product?.soldOut) {
        this.soldOut = true;
        this.releaseBusy();
        return;
      }
      this.quantityEnabled = info.product?.quantityEnabled === true;
      this.maxQuantity = this.quantityEnabled
        ? (info.product?.maxQuantity ?? MAX_QUANTITY_FALLBACK)
        : null;

      // Direct checkout: with exactly one provider and a fixed price, the
      // one-option picker is a step with no job — enter the provider's flow
      // immediately. pickProvider branches on the checkout response, so
      // redirect rails hand off with no popup at all, while multi-step flows
      // (delivery-email prompt, pay-by-mail, in-page crypto) open the popup
      // at their own first real step. Editable amounts (tips, donations,
      // name-your-price) always keep the picker: the amount input lives
      // there. So does a quantity-enabled product — the stepper's home is the
      // popup header. Preview mode always keeps it too — showing the picker is
      // the dashboard preview's whole job, whichever path supplied the providers.
      if (
        !this.isPreview() &&
        info.providers.length === 1 &&
        !this.isEditableAmount() &&
        !this.quantityEnabled
      ) {
        this.directFlow = true;
        this.directEligible = true;
        await this.pickProvider(info.providers[0]);
        this.releaseBusy();
        return;
      }

      this.directFlow = false;
      this.modalState = { kind: 'picking', providers: info.providers };
      this.releaseBusy();
    } catch (err) {
      // surfaceError releases the lock itself, keeping the event order
      // stable for listeners: cm-error first, then the settling cm-idle.
      this.surfaceError(err);
    }
  }

  /** Unlock the trigger and announce settlement. Fired on handoff, popup
   *  open, or error — the paired end to `cm-busy`. */
  private releaseBusy(): void {
    if (!this.busyLock) return;
    this.busyLock = false;
    this.suppressSpinner = false;
    this.dispatchCustomEvent('cm-idle', {});
    this.render();
  }

  /** Header total for a quantity product: unit × quantity with float noise
   *  trimmed (0.1 × 3 renders as 0.3, not 0.30000000000000004). Falls back to
   *  the raw attribute text when it isn't numeric. */
  private quantityTotal(amountAttr: string): string {
    const unit = Number.parseFloat(amountAttr);
    if (!Number.isFinite(unit)) return amountAttr;
    return String(Number.parseFloat((unit * this.quantity).toFixed(8)));
  }

  /** `editable-amount` present and not explicitly "false". */
  private isEditableAmount(): boolean {
    const v = this.getAttribute('editable-amount');
    return v !== null && v !== 'false';
  }

  /**
   * The amount to charge. In editable mode it's the buyer-entered value;
   * otherwise the `amount` attribute. Returns NaN when neither is valid so
   * callers can reject before hitting the API.
   */
  private getEffectiveAmount(): number {
    if (this.isEditableAmount()) {
      return this.editableAmount ?? NaN;
    }
    return Number.parseFloat(this.getAttribute('amount') ?? '');
  }

  private async loadProjectInfo(): Promise<PublicProjectInfo> {
    if (this.cachedProjectInfo) return this.cachedProjectInfo;
    const endpoint = this.getValidatedEndpoint();
    const projectId = this.getRequiredAttr('project-id');
    // Pass product-id so the server can tailor the picker per product
    // (which providers show, their order, their labels). Omitted when the
    // button has no product-id; the server then returns all providers.
    const productId = this.getAttribute('product-id');
    const base = `${endpoint.replace(/\/$/, '')}/api/projects/${encodeURIComponent(projectId)}/public-info`;
    const url = productId ? `${base}?productId=${encodeURIComponent(productId)}` : base;
    const response = await this.fetchImpl(url);
    if (!response.ok) {
      throw new Error(`coin-moebius-buy: public-info responded ${response.status}`);
    }
    const info = (await response.json()) as PublicProjectInfo;
    this.cachedProjectInfo = info;
    return info;
  }

  /**
   * Merchant-supplied delivery email: the `buyer-email` attribute or a
   * `buyerEmail` JS property (for SPAs). A site that already knows the buyer (a
   * members area, a logged-in app) can hand us the address so the buyer never
   * types it. Invalid values are treated as absent, so the prompt still shows
   * as the fallback. The worker re-validates.
   */
  private suppliedEmail(): string | undefined {
    const prop = (this as unknown as { buyerEmail?: unknown }).buyerEmail;
    const raw = (typeof prop === 'string' ? prop : this.getAttribute('buyer-email')) ?? '';
    const trimmed = raw.trim();
    return isValidEmail(trimmed) ? trimmed : undefined;
  }

  /**
   * The price the SERVER says this order is, for the screen we are on, or null
   * when it has not told us one.
   *
   * Three sources, all the server's: what a completed checkout reported, what
   * the chosen rail quoted, and what every rail quoted when they agree. The
   * merchant's page is never one of them. A page can claim any number, and for
   * a fixed-price product the catalog overrides it anyway, so showing the
   * attribute means showing a price that is not being charged.
   *
   * Returns null when rails disagree on price: the picker already labels each
   * one with its own figure, and a single header number would have to be wrong
   * for all but one of them.
   */
  private serverPrice(state: ModalState): { amount: string; currency: string } | null {
    const quoted = (p: { amount: number; currency: string } | undefined) =>
      p && Number.isFinite(p.amount) ? { amount: String(p.amount), currency: p.currency } : null;

    if (state.kind === 'manual-preview' || state.kind === 'manual-confirmed') {
      return {
        amount: String(state.payload.expectedAmount),
        currency: state.payload.expectedCurrency,
      };
    }
    if (state.kind === 'crypto-paid') {
      const paid = state.payload;
      if (paid && typeof paid.expectedAmount === 'number') {
        return {
          amount: String(paid.expectedAmount),
          currency: paid.expectedCurrency ?? this.getAttribute('currency') ?? '',
        };
      }
      return quoted(state.provider.price);
    }
    if (state.kind === 'crypto-pay' && typeof state.payload.expectedAmount === 'number') {
      return {
        amount: String(state.payload.expectedAmount),
        currency: state.payload.expectedCurrency ?? this.getAttribute('currency') ?? '',
      };
    }
    if (
      state.kind === 'crypto-pay' ||
      state.kind === 'picking-coin' ||
      state.kind === 'collect-email'
    ) {
      return quoted(state.provider.price);
    }
    if (state.kind === 'picking') {
      const prices = state.providers.map((p) => p.price).filter((p) => p !== undefined);
      if (prices.length !== state.providers.length || prices.length === 0) return null;
      const first = prices[0];
      const uniform = prices.every(
        (p) => p.amount === first.amount && p.currency === first.currency,
      );
      return uniform ? quoted(first) : null;
    }
    return null;
  }

  private async pickProvider(
    provider: PublicProviderInfo,
    collectedEmail?: string,
    emailPromptDone = false,
  ): Promise<void> {
    try {
      const cancelEvent = this.dispatchCustomEvent('cm-checkout-started', {
        provider: provider.id,
      });
      if (cancelEvent.defaultPrevented) return;

      // Preview mode: never hit the network or charge anything. previewPick()
      // shows the real in-page screen (pay-by-mail, in-page coin) with stand-in
      // data, or a navigate alert for methods that would leave the page.
      if (this.isPreview()) {
        this.previewPick(provider);
        return;
      }

      // A rail with more than one coin cannot mint yet: ZANO and Freedom Dollar
      // are different money at different prices, and only the buyer knows which
      // they hold. Ask, then come back here with the answer.
      const coins = provider.coins ?? [];
      if (coins.length > 1 && this.chosenCoin === null) {
        this.modalState = { kind: 'picking-coin', provider, coins };
        this.render();
        return;
      }
      // One coin left is not a choice, but it still has to be named. Without
      // this the mint decides by price currency, which on a product priced in
      // the coin the merchant turned off would hand back that very coin.
      if (coins.length === 1 && this.chosenCoin === null) {
        this.chosenCoin = coins[0]!.id;
        this.chosenCoinLabel = coins[0]!.label;
      }

      // A merchant who already knows the buyer can supply the address, skipping
      // the prompt on every rail. A popup-collected value (the Continue button
      // re-enters here with it) takes precedence over the supplied one.
      const email = collectedEmail ?? this.suppliedEmail();

      // Delivery email: this provider's payment carries no buyer email, so we
      // collect one before handoff. Only when the server asked for it AND we
      // don't already have an address (supplied or previously collected).
      if (provider.collectEmail && email === undefined) {
        this.modalState = { kind: 'collect-email', provider, error: null, optional: false };
        this.render();
        return;
      }
      // Pay-by-mail: offer an OPTIONAL email (receipt + download on confirmation) once. The
      // buyer can enter one or skip; either way `emailPromptDone` stops us re-asking.
      if (provider.collectEmailOptional && email === undefined && !emailPromptDone) {
        this.modalState = { kind: 'collect-email', provider, error: null, optional: true };
        this.render();
        return;
      }

      const endpoint = this.getValidatedEndpoint();
      const projectId = this.getRequiredAttr('project-id');
      const body: {
        productId: string;
        amount: number;
        currency: string;
        metadata?: { customerRef: string };
        buyerEmail?: string;
        quantity?: number;
        asset?: string;
      } = {
        productId: this.getRequiredAttr('product-id'),
        amount: this.getEffectiveAmount(),
        currency: this.getRequiredAttr('currency'),
      };
      if (email) body.buyerEmail = email;
      if (this.chosenCoin !== null) body.asset = this.chosenCoin;
      if (!Number.isFinite(body.amount) || body.amount <= 0) {
        throw new Error('coin-moebius-buy: a positive amount is required');
      }
      // Quantity products send the picked count and the matching display
      // total. The server recomputes the charge from the catalog either way.
      if (this.quantityEnabled) {
        body.quantity = this.quantity;
        body.amount = body.amount * this.quantity;
      }
      // Optional merchant-supplied opaque identifier. When present, the
      // server forwards it to the provider and stores it alongside the
      // transaction so the merchant can later answer "which of my users
      // paid?" without us holding any actual customer data.
      const customerRef = this.getAttribute('customer-ref');
      if (customerRef !== null && customerRef !== '') {
        body.metadata = { customerRef };
      }

      const response = await this.fetchImpl(
        `${endpoint.replace(/\/$/, '')}/api/checkout/${encodeURIComponent(provider.id)}/${encodeURIComponent(projectId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      if (!response.ok) {
        throw await this.checkoutError(response);
      }

      if (provider.id === 'manual') {
        const payload = (await response.json()) as CheckoutManualPreview;
        this.modalState = { kind: 'manual-preview', payload, provider };
        this.render();
        return;
      }

      // Branch on the RESPONSE SHAPE, not the provider id, so any current or
      // future in-page crypto provider works the same way: an address + a
      // wallet URI means "show the pay-with-crypto window"; a `url` means
      // "redirect". This keeps the button generic across coins.
      const payload = (await response.json()) as Record<string, unknown>;
      const crypto = parseCryptoInstructions(payload, provider);
      if (crypto) {
        this.modalState = {
          kind: 'crypto-pay',
          payload: crypto,
          provider,
          checking: false,
          note: null,
          coinLabel: this.chosenCoinLabel,
        };
        this.render();
        this.startStatusPolling();
        return;
      }

      const url = (payload as { url?: unknown }).url;
      if (typeof url !== 'string' || !url) {
        throw new Error('coin-moebius-buy: checkout response missing redirect URL');
      }
      try {
        const redirectUrl = new URL(url);
        if (redirectUrl.protocol !== 'https:' && redirectUrl.protocol !== 'http:') {
          throw new Error('blocked');
        }
      } catch {
        throw new Error('coin-moebius-buy: checkout redirect URL is not a valid https/http URL');
      }
      this.navigateImpl(url);
    } catch (err) {
      this.surfaceError(err);
    }
  }

  // --- in-page crypto status -------------------------------------------

  /** Start the gentle backstop poll. The "Check payment status" button is the
   *  primary control; this just catches confirmation if the buyer waits. */
  private startStatusPolling(): void {
    this.stopStatusPolling();
    this.statusPollStartedAt = Date.now();
    this.statusTimer = setInterval(() => {
      if (Date.now() - this.statusPollStartedAt > STATUS_POLL_TIMEOUT_MS) {
        this.stopStatusPolling();
        return;
      }
      void this.checkStatus(false);
    }, STATUS_POLL_INTERVAL_MS);
  }

  private stopStatusPolling(): void {
    if (this.statusTimer !== null) {
      clearInterval(this.statusTimer);
      this.statusTimer = null;
    }
  }

  /**
   * Writes into the element rather than calling `render()`, which every second
   * would rebuild the address block and steal focus mid-click.
   */
  private startHoldCountdown(): void {
    this.stopHoldCountdown();
    const tick = (): void => {
      const el = this.shadowRoot?.querySelector<HTMLElement>('[data-hold-until]') ?? null;
      if (el === null) {
        this.stopHoldCountdown();
        return;
      }
      const until = Number(el.dataset.holdUntil);
      const left = timeLeft(until, Date.now());
      if (left === null) {
        // Say it lapsed rather than freezing on 0:00 still claiming a hold.
        el.textContent = 'This price has expired. Check the payment status before sending.';
        el.classList.add('crypto-hold--lapsed');
        this.stopHoldCountdown();
        return;
      }
      el.textContent = `Price held for ${left}`;
    };
    tick();
    this.holdTimer = setInterval(tick, 1000);
  }

  private stopHoldCountdown(): void {
    if (this.holdTimer !== null) {
      clearInterval(this.holdTimer);
      this.holdTimer = null;
    }
  }

  /**
   * Poll the buyer-facing status endpoint once. `manual` = the buyer clicked
   * "Check payment status" (we show "Checking…" and always reflect the result);
   * a background poll only re-renders when the status actually changes, so the
   * QR and focus don't churn every interval.
   */
  private async checkStatus(manual: boolean): Promise<void> {
    const state = this.modalState;
    if (state?.kind !== 'crypto-pay') return;
    // Preview has no real payment to confirm, so the status check is inert — it
    // just keeps the same "waiting" screen rather than hitting the network.
    if (this.isPreview()) {
      this.modalState = {
        ...state,
        checking: false,
        note: 'Waiting for the buyer to send payment.',
      };
      this.render();
      return;
    }
    if (manual) {
      this.modalState = { ...state, checking: true };
      this.render();
    }

    let status: string | undefined;
    let progress: CryptoProgress | null = null;
    let reachable = true;
    try {
      const endpoint = this.getValidatedEndpoint();
      const projectId = this.getRequiredAttr('project-id');
      const url = `${endpoint.replace(/\/$/, '')}/status/${encodeURIComponent(projectId)}/${encodeURIComponent(state.payload.paymentId)}?v=${STATUS_CONTRACT_VERSION}`;
      const response = await this.fetchImpl(url);
      if (response.ok) {
        const result = (await response.json()) as {
          status?: string;
          progress?: {
            state?: string;
            confirmations?: number;
            requiredConfirmations?: number | null;
            asset?: string | null;
            received?: string | null;
            expected?: string | null;
          };
        };
        status = result.status;
        const raw = result.progress;
        progress =
          raw && typeof raw.state === 'string'
            ? {
                state: raw.state,
                confirmations: typeof raw.confirmations === 'number' ? raw.confirmations : null,
                requiredConfirmations:
                  typeof raw.requiredConfirmations === 'number' ? raw.requiredConfirmations : null,
                asset: typeof raw.asset === 'string' ? raw.asset : null,
                received: typeof raw.received === 'string' ? raw.received : null,
                expected: typeof raw.expected === 'string' ? raw.expected : null,
              }
            : null;
      }
    } catch {
      reachable = false;
    }

    // The modal may have closed (or moved on) while we awaited.
    const cur = this.modalState;
    if (cur?.kind !== 'crypto-pay') return;

    if (status === 'success') {
      this.stopStatusPolling();
      this.stopHoldCountdown();
      this.modalState = { kind: 'crypto-paid', provider: cur.provider, payload: cur.payload };
      this.render();
      this.dispatchCustomEvent('cm-success', {
        provider: cur.provider.id,
        paymentId: cur.payload.paymentId,
      });
      return;
    }
    if (status === 'failed') {
      // Stop polling, but stay on a screen that can still be checked. A slow
      // chain can confirm days later, and "start over" here means paying twice.
      this.stopStatusPolling();
      this.stopHoldCountdown();
      this.modalState = { ...cur, checking: false, note: null, progress: { ...EXPIRED_PROGRESS } };
      this.render();
      return;
    }

    const progressState = progress?.state;
    const confirmations = progress?.confirmations ?? undefined;
    const requiredConfirmations = progress?.requiredConfirmations ?? undefined;

    let note = cur.note;
    if (
      status === 'partial' ||
      progressState === 'partial' ||
      (RECEIVED_WITHOUT_WAITING.has(cur.provider.id) && shortOfExpected(progress))
    ) {
      // Spoken status; the face carries the figures when the rail sends them.
      note = 'Part of the payment arrived. Send the rest to the same address.';
    } else if (progressState === 'seen' || progressState === 'confirming') {
      // A bare "waiting" gives the buyer nothing to watch and no idea whether
      // to keep the tab open for one more minute or ten. The worker already
      // reports the count and the target, so say where it is.
      note =
        typeof confirmations === 'number' && typeof requiredConfirmations === 'number'
          ? `Payment received. Confirming on the network, ${confirmations} of ${requiredConfirmations}.`
          : typeof confirmations === 'number'
            ? `Payment received. Confirming on the network, ${confirmations} so far.`
            : 'Payment received. Waiting for the network to confirm it.';
    } else if (manual && !reachable) {
      note = 'Could not reach the server. Check your connection and try again.';
    } else if (manual) {
      note = 'No payment yet. It can take a few minutes after you send.';
    }

    // Only re-render when something user-visible changed; a manual click always
    // re-renders so the "Checking…" button returns to normal.
    const faceChanged = (cur.progress?.state ?? null) !== (progress?.state ?? null);
    const countChanged =
      (cur.progress?.confirmations ?? null) !== (progress?.confirmations ?? null);
    if (manual || note !== cur.note || faceChanged || countChanged) {
      this.modalState = { ...cur, checking: false, note, progress };
      this.render();
    }
  }

  /**
   * Whether the button is in preview mode (the dashboard's "preview the buy
   * button" affordance). Inert: real providers and prices render, but picking
   * one never starts a checkout or creates a transaction.
   */
  private isPreview(): boolean {
    return this.hasAttribute('preview') && this.getAttribute('preview') !== 'false';
  }

  /**
   * Whether to show the "Powered by Coin Moebius" badge. In preview the
   * dashboard supplies the value (it never fetches public-info); live, it comes
   * from the server's `showBranding` on the cached project info.
   */
  private shouldShowBranding(): boolean {
    if (this.isPreview()) return this.previewBranding;
    return this.cachedProjectInfo?.showBranding === true;
  }

  /** Effective amount + currency for a preview screen: the per-method price
   *  from public-info when present, else the button's own amount/currency. */
  private previewAmount(provider: PublicProviderInfo): { amount: number; currency: string } {
    if (provider.price) return { amount: provider.price.amount, currency: provider.price.currency };
    return {
      amount: this.getEffectiveAmount(),
      currency: this.getAttribute('currency') ?? '',
    };
  }

  /**
   * Preview = the live button, with the one unavoidable exception that we can't
   * actually take money or leave the page. So each method shows exactly what a
   * buyer would see: pay-by-mail shows the real mailing screen, an in-page coin
   * (Monero) shows the real address + QR screen with stand-in data, and a method
   * that would navigate away (cards, hosted crypto) shows a short alert saying
   * so. No "preview" wording, no fake "this would open X" screens.
   */
  private previewPick(provider: PublicProviderInfo): void {
    const { amount, currency } = this.previewAmount(provider);
    if (provider.id === 'manual') {
      this.modalState = {
        kind: 'manual-preview',
        provider,
        payload: {
          preview: true,
          mailingAddress: provider.mailingAddress ?? '',
          expectedAmount: amount,
          expectedCurrency: currency,
          ...(provider.humanInstructions !== undefined
            ? { instructions: provider.humanInstructions }
            : {}),
        },
      };
    } else if (PREVIEW_IN_PAGE_CRYPTO.has(provider.id)) {
      this.modalState = {
        kind: 'crypto-pay',
        provider,
        checking: false,
        note: null,
        payload: samplePreviewCrypto(provider),
      };
    } else {
      // Navigates away in a live checkout. Preview can't leave the page, so keep
      // the picker open and show a banner at the top of the list saying so.
      this.previewNotice = `Opens ${cryptoMethodName(provider)} to finish paying.`;
    }
    this.render();
  }

  /** Banner shown at the top of the picker for methods that navigate away in a
   *  live checkout (preview can't leave the page). Cleared when the popup closes. */
  private previewNotice: string | null = null;

  // --- Stock + quantity state -------------------------------------------
  // Sold-out latch: set from public-info on click (or a PRODUCT_SOLD_OUT
  // checkout rejection). While set, the trigger renders disabled as
  // "Sold out" and clicks do nothing. Cleared only by a fresh page load.
  private soldOut = false;
  // Stepper facts from public-info: whether to render the quantity stepper
  // and its ceiling (server caps at effective stock / 99).
  private quantityEnabled = false;
  private maxQuantity: number | null = null;
  // The buyer's picked quantity. Reset to 1 whenever the popup closes.
  private quantity = 1;
  // Banner over the picker after an INSUFFICIENT_STOCK rejection ("Only N
  // left…"), shown while the buyer retries at the clamped quantity.
  private stockNotice: string | null = null;

  private async confirmManualPayment(): Promise<void> {
    // Preview mode: skip the network entirely and show a placeholder
    // reference code so the merchant sees the full pay-by-mail flow.
    if (this.isPreview()) {
      const currency = this.getAttribute('currency') ?? '';
      const state = this.modalState;
      const amount =
        state?.kind === 'manual-preview' ? state.payload.expectedAmount : this.getEffectiveAmount();
      const mailingAddress = state?.kind === 'manual-preview' ? state.payload.mailingAddress : '';
      const instructions =
        state?.kind === 'manual-preview' ? state.payload.instructions : undefined;
      this.modalState = {
        kind: 'manual-confirmed',
        payload: {
          txId: 'preview',
          referenceCode: 'TXG-4HM-9KPR',
          mailingAddress,
          expectedAmount: amount,
          expectedCurrency: currency,
          ...(instructions !== undefined ? { instructions } : {}),
        },
      };
      this.render();
      return;
    }
    // Captured before the loading state replaces it, so the confirmed screen
    // can still name the seller's arrival window.
    const previewState = this.modalState;
    const manualProvider =
      previewState?.kind === 'manual-preview' ? previewState.provider : undefined;
    try {
      const endpoint = this.getValidatedEndpoint();
      const projectId = this.getRequiredAttr('project-id');
      const body: {
        productId: string;
        amount: number;
        currency: string;
        metadata?: { customerRef: string };
        quantity?: number;
      } = {
        productId: this.getRequiredAttr('product-id'),
        amount: this.getEffectiveAmount(),
        currency: this.getRequiredAttr('currency'),
      };
      if (!Number.isFinite(body.amount) || body.amount <= 0) {
        throw new Error('coin-moebius-buy: a positive amount is required');
      }
      // Same quantity handling as the main checkout path: send the picked
      // count and matching display total; the server recomputes from catalog.
      if (this.quantityEnabled) {
        body.quantity = this.quantity;
        body.amount = body.amount * this.quantity;
      }
      const customerRef = this.getAttribute('customer-ref');
      if (customerRef !== null && customerRef !== '') {
        body.metadata = { customerRef };
      }

      this.modalState = { kind: 'loading' };
      this.render();

      const response = await this.fetchImpl(
        `${endpoint.replace(/\/$/, '')}/api/checkout/manual/${encodeURIComponent(projectId)}/confirm`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      if (!response.ok) {
        throw await this.checkoutError(response);
      }

      const payload = (await response.json()) as CheckoutManualConfirmed;
      this.modalState = {
        kind: 'manual-confirmed',
        payload,
        ...(manualProvider ? { provider: manualProvider } : {}),
      };
      this.render();
    } catch (err) {
      this.surfaceError(err);
    }
  }

  /**
   * Turn a failed checkout response into a buyer-friendly error. A product that
   * was removed (or a project/product that no longer exists) becomes a neutral
   * "no longer available" message that doesn't blame the shopper or the seller,
   * instead of a raw status code. The server's code/message/status are preserved
   * on `error.cause` so a merchant listening to `cm-error` can react (e.g. pull a
   * dead button) without the shopper ever seeing the internals.
   */
  private async checkoutError(response: Response): Promise<Error> {
    let code: string | undefined;
    let serverMessage: string | undefined;
    let details: Record<string, unknown> | undefined;
    try {
      const body = (await response.json()) as {
        error?: { code?: string; message?: string; details?: Record<string, unknown> };
      };
      code = body.error?.code;
      serverMessage = body.error?.message;
      details = body.error?.details;
    } catch {
      // Non-JSON body — fall back to status-only signals below.
    }
    const unavailable =
      response.status === 404 ||
      response.status === 410 ||
      code === 'PRODUCT_NOT_IN_CATALOG' ||
      code === 'PRODUCT_UNAVAILABLE' ||
      code === 'PROJECT_NOT_FOUND';
    let message = unavailable
      ? 'This item is no longer available. Please check back later.'
      : 'We couldn’t start checkout. Please try again in a moment.';
    let insufficientStock: number | undefined;
    if (code === 'PRODUCT_SOLD_OUT') {
      // Latch: the trigger renders disabled as "Sold out" once the popup closes.
      message = SOLD_OUT_MESSAGE;
      this.soldOut = true;
    } else if (code === 'INSUFFICIENT_STOCK') {
      // The server says how many remain; surfaceError clamps the stepper and
      // reopens the picker so the buyer can retry without starting over.
      const available = Number(details?.['available']);
      insufficientStock = Number.isInteger(available) && available > 0 ? available : 1;
      message =
        serverMessage ?? `Only ${insufficientStock} left. Please choose a smaller quantity.`;
    }
    const error = new Error(message);
    (error as { cause?: unknown }).cause = {
      status: response.status,
      code,
      message: serverMessage,
    };
    if (insufficientStock !== undefined) {
      (error as { insufficientStock?: number }).insufficientStock = insufficientStock;
    }
    return error;
  }

  private surfaceError(err: unknown): void {
    const message = err instanceof Error ? err.message : String(err);
    // Cancelable: preventDefault() suppresses the built-in error display so a
    // developer can route the message into their own toast/UI instead.
    const event = new CustomEvent('cm-error', {
      detail: { error: err },
      bubbles: true,
      composed: true,
      cancelable: true,
    });
    this.dispatchEvent(event);
    this.releaseBusy();
    if (event.defaultPrevented) return;
    // Not enough stock for the picked quantity: clamp the stepper to what's
    // left and reopen the picker with the "Only N left" banner, so the buyer
    // retries at the lower count without starting over.
    const shortage = (err as { insufficientStock?: number }).insufficientStock;
    if (shortage !== undefined && this.cachedProjectInfo) {
      this.maxQuantity = shortage;
      this.quantity = Math.min(this.quantity, shortage);
      this.stockNotice = message;
      this.modalState = { kind: 'picking', providers: this.cachedProjectInfo.providers };
      this.render();
      return;
    }
    // Direct flow with no popup on screen: a quiet alert line under the
    // button, cleared on the next click. Anything already inside a popup
    // (picker, pay-by-mail, crypto, email prompt) keeps erroring in place.
    if (this.directFlow && this.modalState === null) {
      this.inlineError = message;
    } else {
      this.modalState = { kind: 'error', message };
    }
    this.render();
  }

  private dispatchCustomEvent(name: string, detail?: Record<string, unknown>): CustomEvent {
    const event = new CustomEvent(name, {
      detail: detail ?? {},
      bubbles: true,
      composed: true,
      cancelable: true,
    });
    this.dispatchEvent(event);
    return event;
  }

  private closeModal(): void {
    this.modalState = null;
    this.stopStatusPolling();
    // Forget the buyer's typed amount so reopening re-seeds the suggestion, and
    // reset "touched" so a reopened popup doesn't show the prompt until they type.
    this.editableAmount = null;
    this.amountTouched = false;
    this.previewNotice = null;
    // Forget the coin, so a reopened popup asks again rather than silently
    // charging whatever was picked last time.
    this.chosenCoin = null;
    this.chosenCoinLabel = null;
    // Reset the stepper: a reopened popup starts back at one unit, with any
    // "Only N left" banner cleared (the clamped max survives via public-info).
    this.quantity = 1;
    this.stockNotice = null;
    // Tear down the trap before re-rendering — otherwise the listener
    // stays bound to a detached node.
    if (this.modalKeydownHandler) {
      (this.shadowRoot as unknown as EventTarget | null)?.removeEventListener(
        'keydown',
        this.modalKeydownHandler,
      );
      this.modalKeydownHandler = null;
    }
    this.render();

    // Restore focus to whatever held it before the modal opened. Defer
    // one frame so the DOM has settled — focusing a button that's about
    // to be re-rendered fails silently.
    const restore = this.triggerForRestore;
    this.triggerForRestore = null;
    if (restore && typeof restore.focus === 'function') {
      queueMicrotask(() => restore.focus());
    }
  }

  /**
   * Focus-trap implementation. While the modal is open:
   *   - `Escape` closes the modal.
   *   - `Tab` cycles forward through focusable elements within the dialog.
   *   - `Shift+Tab` cycles backward.
   * Focus can't escape the dialog; that's the contract of a modal dialog.
   */
  private installFocusTrap(shadow: ShadowRoot): void {
    if (this.modalKeydownHandler) {
      (shadow as unknown as EventTarget).removeEventListener('keydown', this.modalKeydownHandler);
    }
    this.modalKeydownHandler = (event: Event) => {
      const kbd = event as KeyboardEvent;
      if (kbd.key === 'Escape') {
        kbd.preventDefault();
        this.closeModal();
        return;
      }
      if (kbd.key !== 'Tab') return;
      const focusables = this.collectFocusables(shadow);
      if (focusables.length === 0) return;

      const active = shadow.activeElement as HTMLElement | null;
      const currentIndex = active ? focusables.indexOf(active) : -1;
      const dir = kbd.shiftKey ? -1 : 1;
      let nextIndex = currentIndex + dir;
      if (nextIndex < 0) nextIndex = focusables.length - 1;
      if (nextIndex >= focusables.length) nextIndex = 0;

      kbd.preventDefault();
      focusables[nextIndex].focus();
    };
    (shadow as unknown as EventTarget).addEventListener('keydown', this.modalKeydownHandler);
  }

  private collectFocusables(shadow: ShadowRoot): HTMLElement[] {
    const selectors = [
      'button:not([disabled])',
      '[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    const dialog = shadow.querySelector<HTMLElement>('.dialog');
    if (!dialog) return [];
    return Array.from(dialog.querySelectorAll<HTMLElement>(selectors));
  }

  // --- rendering -------------------------------------------------------

  private render(): void {
    const shadow = this.shadowRoot;
    if (!shadow) return;

    // When an explicit `label` attribute is set, use it directly. Otherwise
    // project the element's light-DOM children through a native <slot> so
    // the slotted text appears synchronously as the parser adds it (no
    // "Buy" → real-label flash), with DEFAULT_LABEL as the fallback only
    // when there's genuinely no inner text.
    const labelAttr = this.getAttribute('label');
    // Sold out overrides whatever label the merchant set: the state IS the
    // message, and the trigger below also renders disabled.
    const triggerInner = this.soldOut
      ? escapeHtml(SOLD_OUT_LABEL)
      : labelAttr !== null
        ? escapeHtml(labelAttr || DEFAULT_LABEL)
        : `<slot>${escapeHtml(DEFAULT_LABEL)}</slot>`;

    // Guarantee the trigger always has an accessible name. With a `label` or
    // real slotted text it's named already. The gap is icon-only slotted
    // content (e.g. an <svg> with no text): the <slot> fallback "Buy" is
    // suppressed because the slot has assigned nodes, leaving the button
    // unnamed. Detect that and supply an aria-label so the button is never
    // announced as just "button".
    const slotText = (this.textContent ?? '').trim();
    const needsFallbackName = labelAttr === null && slotText === '';
    const hasIconOnlyContent = needsFallbackName && this.childElementCount > 0;
    const triggerAriaLabel = needsFallbackName ? ` aria-label="${escapeAttr(DEFAULT_LABEL)}"` : '';
    if (hasIconOnlyContent) {
      console.warn(
        `coin-moebius-buy: the button has no text, so it falls back to the accessible name "${DEFAULT_LABEL}". Set a "label" attribute to give it a meaningful name.`,
      );
    }
    const modalHtml = this.renderModal();

    // Busy dressing on the trigger itself (never a popup): dimmed label under
    // a centered spinner, unless the merchant turned the spinner off or a
    // cm-busy listener took over the loading UI. aria-busy carries the state
    // for assistive tech and the CSS lock either way.
    const showSpinner = this.busyLock && this.spinner && !this.suppressSpinner;
    const busyAttrs = this.busyLock ? ' aria-busy="true"' : '';
    const disabledAttr = this.disabled || this.soldOut ? ' disabled' : '';
    const spinnerHtml = showSpinner
      ? `<span class="cm-spinner" part="spinner" aria-hidden="true"><slot name="spinner"><span class="cm-spinner-ring"></span></slot></span>`
      : '';

    // Under-button credit: only once the server has told us to show branding
    // AND the button has qualified for direct checkout (where the popup badge
    // would otherwise never be seen). No part, no variable — see STYLES note.
    const inlineBranding =
      this.directEligible && this.shouldShowBranding()
        ? `<a class="cm-branding--inline" href="${BRANDING_HREF}" target="_blank" rel="noopener noreferrer">${BRANDING_LABEL}</a>`
        : '';

    const inlineErrorHtml = this.inlineError
      ? `<p class="cm-inline-error" part="error" role="alert">${escapeHtml(this.inlineError)}</p>`
      : '';

    // innerHTML replacement drops focus from the old trigger; if the buyer's
    // focus was on it (it always is mid-click), put it back on the new one so
    // keyboard users aren't dumped to the top of the document.
    const hadTriggerFocus = shadow.activeElement?.getAttribute?.('part') === 'button';

    shadow.innerHTML = `
<style>${STYLES}</style>
<button part="button" type="button"${triggerAriaLabel}${busyAttrs}${disabledAttr}><span class="cm-label">${triggerInner}</span>${spinnerHtml}</button>
${inlineErrorHtml}
${inlineBranding}
${modalHtml}
`;

    const trigger = shadow.querySelector<HTMLElement>('button[part="button"]');
    trigger?.addEventListener('click', () => {
      void this.handleClick();
    });
    if (hadTriggerFocus && trigger) {
      trigger.focus({ preventScroll: true });
    }

    this.wireModalEvents(shadow);
  }

  private renderModal(): string {
    const state = this.modalState;
    if (state === null) return '';

    // Header amount: the static attribute by default, but every screen AFTER
    // the buyer picks a method shows the price they picked. On the pay-by-mail
    // screens that's the SERVER's expected amount (which reflects a
    // buyer-entered editable value); on the email screen in between it's the
    // picked method's own price (or the effective amount when the method has
    // none). Otherwise the header would still show the product's base price
    // while the surrounding screens show the real one.
    // The page is not allowed to decide what price the buyer is shown. For a
    // fixed-price product the server charges the catalog price and ignores the
    // `amount` attribute entirely, so trusting that attribute puts a number on
    // screen that nobody is charging: a page saying 1.00 over a $10 order.
    // Whenever the server has told us a price, it wins. The attribute is the
    // fallback only for a variable-price product, where the buyer's own entry
    // genuinely is the price, and for an older worker that sends none.
    const server = this.serverPrice(state);
    let amount = server?.amount ?? this.getAttribute('amount') ?? '';
    let currency = server?.currency ?? this.getAttribute('currency') ?? '';
    if (state.kind === 'manual-preview' || state.kind === 'manual-confirmed') {
      amount = String(state.payload.expectedAmount);
      currency = state.payload.expectedCurrency;
    } else if (state.kind === 'collect-email') {
      const unit = state.provider.price?.amount ?? this.getEffectiveAmount();
      if (Number.isFinite(unit)) {
        amount = this.quantityEnabled ? this.quantityTotal(String(unit)) : String(unit);
      }
      currency = state.provider.price?.currency ?? currency;
    }
    const isConfirmed =
      state.kind === 'manual-confirmed' ||
      state.kind === 'crypto-paid' ||
      receivedWithoutWaiting(state);

    let body = '';
    if (state.kind === 'loading') {
      body = `<p role="status" style="text-align:center;opacity:0.7;">Loading payment options…</p>`;
    } else if (state.kind === 'error') {
      // role="alert" announces the message assertively the moment it renders,
      // which is what you want for a checkout failure (the polite live region
      // wrapping the body would queue behind other updates).
      body = `<p class="error" part="error" role="alert">${escapeHtml(state.message)}</p>`;
    } else if (state.kind === 'picking-coin') {
      body = `
				<div class="providers" part="providers" role="group" aria-label="Choose a coin">
					${state.coins
            .map(
              (coin) =>
                `<button class="provider" part="provider" type="button" data-coin="${escapeAttr(coin.id)}">${escapeHtml(coin.label)}</button>`,
            )
            .join('')}
				</div>
				<p class="crypto-status" role="status">Pick the coin you are paying with. The address and the amount depend on it.</p>
			`;
    } else if (state.kind === 'picking') {
      // When a merchant prices the product differently per payment method,
      // show each method's own price on its button. If every method shares
      // the same price the header amount already covers it, so we keep the
      // buttons clean and skip the per-button chip.
      const pricesVary = providerPricesVary(state.providers);
      const notice = this.stockNotice ?? this.previewNotice;
      const noticeBanner = notice
        ? `<p class="cm-notice" part="notice" role="status">${escapeHtml(notice)}</p>`
        : '';
      body = `
				${noticeBanner}
				<div class="providers" role="group" aria-label="Payment methods">
					${state.providers
            .map((p) => {
              // A merchant-set custom label owns the whole button; otherwise the
              // server's buyer-facing name is used. Either way the label stands
              // alone: an emoji renders as a different picture on every
              // platform, and a padlock beside a PayPal P read as clip art
              // rather than a payment choice.
              const customLabel = p.label !== undefined && p.label !== '' ? p.label : null;
              const name = customLabel ?? p.name;
              const priceText = pricesVary && p.price ? `${formatProviderPrice(p.price)}` : '';
              const priceHtml =
                pricesVary && p.price
                  ? `<span class="provider-price" part="provider-price">${providerPriceHtml(p.price)}</span>`
                  : '';
              const ariaLabel = priceText ? `Pay with ${name}, ${priceText}` : `Pay with ${name}`;
              return `
								<button
									class="provider"
									part="provider"
									data-provider-id="${escapeAttr(p.id)}"
									type="button"
									aria-label="${escapeAttr(ariaLabel)}"
								>
									
									<span class="provider-name" part="provider-name">${escapeHtml(name)}</span>
									${priceHtml}
								</button>
							`;
            })
            .join('')}
				</div>
			`;
    } else if (state.kind === 'manual-preview') {
      body = `
				<div part="instructions">
					<div class="manual-section">
						<h3 class="manual-label">Send to</h3>
						<p class="manual-value manual-address">${escapeHtml(state.payload.mailingAddress)}</p>
					</div>
					<div class="manual-section">
						<h3 class="manual-label">Amount due</h3>
						<p class="manual-value">${escapeHtml(String(state.payload.expectedAmount))} ${currencyHtml(state.payload.expectedCurrency)}</p>
					</div>
					${state.payload.instructions ? `<div class="manual-section"><h3 class="manual-label">Instructions</h3><p class="instructions-text">${escapeHtml(state.payload.instructions)}</p></div>` : ''}
					<button class="confirm-btn" data-confirm="manual" part="provider" type="button">Confirm, I'll send payment</button>
				</div>
			`;
    } else if (state.kind === 'manual-confirmed') {
      body = `
				<div part="instructions">
					<div class="manual-section">
						<h3 class="manual-label">Your reference code</h3>
						<div class="ref-code">${escapeHtml(state.payload.referenceCode)}</div>
						<p class="ref-note">Include this code with your payment</p>
					</div>
					<div class="manual-section">
						<h3 class="manual-label">Send to</h3>
						<p class="manual-value manual-address">${escapeHtml(state.payload.mailingAddress)}</p>
					</div>
					<div class="manual-section">
						<h3 class="manual-label">Amount due</h3>
						<p class="manual-value">${escapeHtml(String(state.payload.expectedAmount))} ${currencyHtml(state.payload.expectedCurrency)}</p>
					</div>
					${state.payload.instructions ? `<div class="manual-section"><h3 class="manual-label">Instructions</h3><p class="instructions-text">${escapeHtml(state.payload.instructions)}</p></div>` : ''}
					${
            typeof state.provider?.stockHoldDays === 'number'
              ? `<p class="manual-window" part="manual-window">The seller allows ${escapeHtml(String(state.provider.stockHoldDays))} ${state.provider.stockHoldDays === 1 ? 'day' : 'days'} for it to arrive.</p>`
              : ''
          }
					${receiptBlock(state.payload.receiptUrl)}
					<p class="success-check" role="status">Payment recorded. We'll confirm once received.</p>
				</div>
			`;
    } else if (state.kind === 'crypto-pay') {
      const p = state.payload;
      const amountText =
        p.displayAmount !== null ? `Send ${p.displayAmount} ${p.coin}` : `Send ${p.coin}`;
      const note =
        state.note ?? 'Waiting for your payment. It can take a few minutes after you send.';
      const prog = state.progress ?? null;
      const chain = state.coinLabel ?? p.coin;

      // Address comes down once money is sent, or a nervous buyer pays twice.
      if (receivedWithoutWaiting(state)) {
        body = `
				<div part="instructions">
					${receivedAmountLine(p, prog)}
					<p class="success-check" role="status">✓ Payment received. Thank you!</p>
					${receiptBlock(p.receiptUrl)}
				</div>
			`;
      } else if (prog !== null && (prog.state === 'seen' || prog.state === 'confirming')) {
        const done = prog.confirmations ?? 0;
        const needed = prog.requiredConfirmations;
        const left =
          needed !== null && needed > done
            ? Math.max(1, Math.round((needed - done) * (BLOCK_MINUTES[state.provider.id] ?? 2)))
            : null;
        const ring =
          needed !== null
            ? `<p class="crypto-count" part="crypto-count"><span class="crypto-count__done">${escapeHtml(String(done))}</span> of ${escapeHtml(String(needed))}</p>`
            : `<p class="crypto-count" part="crypto-count"><span class="crypto-count__done">${escapeHtml(String(done))}</span> so far</p>`;
        body = `
				<div part="instructions">
					<p class="crypto-headline" part="crypto-headline">Payment sent</p>
					<p class="crypto-sub" part="crypto-sub">confirming on the ${escapeHtml(chain)} network</p>
					${ring}
					${left === null ? '' : `<p class="crypto-eta" part="crypto-eta">About ${escapeHtml(String(left))} ${left === 1 ? 'minute' : 'minutes'} left</p>`}
					<p class="crypto-reassure" part="crypto-reassure">You can close this. Your payment is already on its way.</p>
					${receiptBlock(p.receiptUrl)}
					<button class="confirm-btn" part="provider" data-check-status type="button"${state.checking ? ' disabled' : ''}>${state.checking ? 'Checking…' : 'Check payment status'}</button>
					<p class="crypto-status" part="crypto-status" role="status">${escapeHtml(note)}</p>
				</div>
			`;
      } else if (prog !== null && prog.state === 'expired') {
        body = `
				<div part="instructions">
					<p class="crypto-headline" part="crypto-headline">This order's window closed</p>
					<p class="crypto-explain" part="crypto-explain">If you already sent it, your money is not lost. Slow networks can take hours or days, and we are still watching for it.</p>
					${receiptBlock(p.receiptUrl)}
					<button class="confirm-btn" part="provider" data-check-status type="button"${state.checking ? ' disabled' : ''}>${state.checking ? 'Checking…' : 'Check payment status'}</button>
					<p class="crypto-status" part="crypto-status" role="status">Check back any time.</p>
				</div>
			`;
      } else if (prog !== null && prog.state === 'late') {
        body = `
				<div part="instructions">
					<p class="crypto-headline" part="crypto-headline">Payment arrived late</p>
					<p class="crypto-explain" part="crypto-explain">This came in after the window for this order closed. Your money is not lost, and ${escapeHtml(cryptoMethodName(state.provider))} has been told.</p>
					${receiptBlock(p.receiptUrl)}
					<p class="crypto-status" part="crypto-status" role="status">Nothing more to do.</p>
				</div>
			`;
      } else if (prog !== null && prog.state === 'partial') {
        // Same address: topping up is one more send, not a new order.
        const asked = prog.expected;
        const got = prog.received;
        const short = shortfall(asked, got);
        const unit = prog.asset ?? p.coin;
        const row = (label: string, value: string): string =>
          `<div class="crypto-row" part="crypto-row"><span class="crypto-row__label">${escapeHtml(label)}</span><span class="crypto-row__value">${escapeHtml(value)}</span></div>`;
        const figures =
          asked !== null && got !== null
            ? row('Asked for', `${asked} ${unit}`) +
              row('Arrived', `${got} ${unit}`) +
              (short === null ? '' : row('Still needed', `${short} ${unit}`))
            : '';
        body = `
				<div part="instructions">
					<p class="crypto-headline" part="crypto-headline">Almost there</p>
					<p class="crypto-sub" part="crypto-sub">on the ${escapeHtml(chain)} network</p>
					<p class="crypto-explain" part="crypto-explain">${
            got !== null
              ? `${escapeHtml(got)} ${escapeHtml(unit)} arrived. That is under the amount for this order, so it is not finished yet.`
              : 'Part of the payment arrived, so this order is not finished yet.'
          }</p>
					<div class="crypto-figures" part="crypto-figures">${figures}</div>
					${short === null ? '' : `<p class="crypto-amount" part="crypto-amount">Send ${escapeHtml(short)} ${escapeHtml(unit)}</p>`}
					<p class="crypto-sub" part="crypto-sub">Same address, and send it from the same wallet</p>
					<div class="crypto-address" part="crypto-address">
						<span class="crypto-address__value">${escapeHtml(p.address)}</span>
						<button class="copy-btn" part="copy" data-copy="${escapeAttr(p.address)}" type="button" aria-label="Copy address">Copy</button>
					</div>
					<button class="confirm-btn" part="provider" data-check-status type="button"${state.checking ? ' disabled' : ''}>${state.checking ? 'Checking…' : 'Check payment status'}</button>
					<p class="crypto-status" part="crypto-status" role="status">Waiting for the rest.</p>
				</div>
			`;
      } else {
        const hold =
          typeof p.expiresAt === 'number' &&
          quoteFloats(p) &&
          timeLeft(p.expiresAt, Date.now()) !== null
            ? `<p class="crypto-hold" part="crypto-hold" role="status" data-hold-until="${escapeAttr(String(p.expiresAt))}"></p>`
            : '';
        body = `
				<div part="instructions">
					<div class="crypto-qr" part="crypto-qr" aria-hidden="true">${renderQrSvg(p.uri)}</div>
					<p class="crypto-amount" part="crypto-amount">${escapeHtml(amountText)}</p>
					${hold}
					<div class="crypto-address" part="crypto-address">
						<span class="crypto-address__value">${escapeHtml(p.address)}</span>
						<button class="copy-btn" part="copy" data-copy="${escapeAttr(p.address)}" type="button" aria-label="Copy address">Copy</button>
					</div>
					<a class="crypto-link" part="crypto-link" href="${escapeAttr(p.uri)}">Open in wallet app</a>
					<button class="confirm-btn" part="provider" data-check-status type="button"${state.checking ? ' disabled' : ''}>${state.checking ? 'Checking…' : 'Check payment status'}</button>
					<p class="crypto-status" part="crypto-status" role="status">${escapeHtml(note)}</p>
				</div>
			`;
      }
    } else if (state.kind === 'crypto-paid') {
      // Name the coin and the amount that actually moved. A buyer who just sent
      // 1.615509 ZANO wants that figure back, not a bare "confirmed": this is
      // their receipt, and it is what they compare against their wallet history
      // if anything looks wrong later.
      body = `
				<div part="instructions">
					${receivedAmountLine(state.payload)}
					<p class="success-check" role="status">✓ Payment received. Thank you!</p>
				</div>
			`;
    } else if (state.kind === 'collect-email') {
      // Required (download rails) vs optional (pay-by-mail): the optional copy makes clear
      // it can be skipped, and adds a "No thanks" button that proceeds without an email.
      const lead = state.optional
        ? "Want an email when your payment is marked received? You'll get a receipt, and your download if this is a digital product. It's optional — we use it only for that one email, then delete it."
        : 'We need an email so we can send you your download. We use this address only to email your files, then delete it. We never store it or use it for anything else.';
      body = `
				<div part="instructions">
					<p class="cm-collect-lead" part="collect-lead">${lead}</p>
					<input class="cm-email-input" part="email-input" type="email" inputmode="email" autocomplete="email" spellcheck="false" placeholder="you@example.com" aria-label="Email address" />
					${state.error ? `<p class="error" part="error" role="alert">${escapeHtml(state.error)}</p>` : ''}
					<button class="confirm-btn" part="provider" data-collect-email-continue type="button">${state.optional ? 'Email me' : 'Continue'}</button>
					${state.optional ? '<button class="cm-skip-btn" part="skip" data-collect-email-skip type="button">No thanks</button>' : ''}
				</div>
			`;
    }

    return `
			<dialog class="modal" part="modal">
				<div
					class="dialog"
					part="dialog"
					role="dialog"
					aria-modal="true"
					aria-labelledby="cm-dialog-title"
					${state.kind === 'loading' ? 'aria-busy="true"' : ''}
				>
					<div class="dialog__header">
						<h2 class="dialog__title" id="cm-dialog-title">
							${escapeHtml(dialogTitle(state))}
						</h2>
						${this.renderAmountHeader(state.kind, amount, currency)}
					</div>
					<div aria-live="polite" aria-atomic="true">
						${body}
					</div>
					<button class="close-btn" part="close" type="button">${isConfirmed ? 'Done' : 'Cancel'}</button>
					${
            this.shouldShowBranding()
              ? // Deliberately no part: the badge is the free tier's price, so
                // page CSS gets no door to it (see the STYLES note).
                `<a class="cm-branding" href="${BRANDING_HREF}" target="_blank" rel="noopener noreferrer">${BRANDING_LABEL}</a>`
              : ''
          }
				</div>
			</dialog>
		`;
  }

  /**
   * The amount line in the dialog header. In editable mode at the picker
   * step it's an input the buyer fills (tip jars, name-your-price); otherwise
   * it's the static amount. Manual preview/confirm always show the static
   * value since the amount is locked once a provider is chosen.
   */
  private renderAmountHeader(kind: ModalState['kind'], amount: string, currency: string): string {
    // Quantity stepper (fixed-price products with the merchant's toggle on):
    // minus / count / plus, with the header amount showing unit × quantity.
    // syncQuantity() updates count/total/disabled in place so the buttons keep
    // focus between presses. Editable-amount products never get here (the
    // toggle is server-rejected for them), so the two header modes don't clash.
    if (this.quantityEnabled && kind === 'picking') {
      const max = this.maxQuantity ?? MAX_QUANTITY_FALLBACK;
      return `
				<div class="dialog__qty" role="group" aria-label="Quantity">
					<button class="qty-btn qty-btn--minus" part="qty-button" type="button" aria-label="Decrease quantity"${this.quantity <= 1 ? ' disabled' : ''}>&minus;</button>
					<span class="qty-value" aria-live="polite">${this.quantity}</span>
					<button class="qty-btn qty-btn--plus" part="qty-button" type="button" aria-label="Increase quantity"${this.quantity >= max ? ' disabled' : ''}>+</button>
				</div>
				${
          amount && currency
            ? `<p class="dialog__amount"><span class="qty-total">${escapeHtml(this.quantityTotal(amount))}</span><span class="currency">${currencyHtml(currency)}</span></p>`
            : ''
        }
			`;
    }
    if (this.isEditableAmount() && kind === 'picking') {
      const seeded = this.editableAmount !== null ? String(this.editableAmount) : amount;
      // The prompt is guidance, not an error: it stays hidden on first open and
      // appears only once the buyer has interacted and left the amount invalid.
      // syncAmount() keeps it in step as they type.
      const showHint = this.editableAmount === null && this.amountTouched;
      return `
				<div class="dialog__amount-edit">
					<input
						class="amount-input"
						part="amount-input"
						type="number"
						inputmode="decimal"
						min="0"
						step="0.01"
						value="${escapeAttr(seeded)}"
						placeholder="0.00"
						aria-label="Amount in ${escapeAttr(currency)}"
						aria-describedby="cm-amount-error"
						aria-invalid="${showHint ? 'true' : 'false'}"
					/>
					<span class="amount-input__currency">${currencyHtml(currency)}</span>
				</div>
				<span class="amount-hint" id="cm-amount-error" role="status"${showHint ? '' : ' hidden'}>Enter an amount to continue.</span>
			`;
    }
    return amount && currency
      ? `<p class="dialog__amount">${escapeHtml(amount)}<span class="currency">${currencyHtml(currency)}</span></p>`
      : '';
  }

  private wireModalEvents(shadow: ShadowRoot): void {
    // No modal currently mounted — nothing to wire and no trap needed.
    if (this.modalState === null) return;

    const close = shadow.querySelector('.close-btn');
    close?.addEventListener('click', () => this.closeModal());

    const overlay = shadow.querySelector<HTMLDialogElement>('.modal');
    if (overlay) {
      // Promote to the browser top layer so the popup paints above every
      // element on the host page. A new <dialog> is built on each render(), so
      // it's never already open; showModal() throws only if the element isn't
      // connected yet, in which case we fall back to a bare `open` attribute
      // (the dialog then renders inline rather than in the top layer).
      if (!overlay.open) {
        if (typeof overlay.showModal === 'function') {
          try {
            overlay.showModal();
          } catch {
            overlay.setAttribute('open', '');
          }
        } else {
          overlay.setAttribute('open', '');
        }
      }
      // Native Escape on a modal dialog fires `cancel` and would close it out
      // from under our state. Route it through closeModal() so modalState, the
      // status poll, and focus restoration all unwind together.
      overlay.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeModal();
      });
      overlay.addEventListener('click', (event) => {
        if (event.target === overlay) this.closeModal();
      });
    }

    const providerButtons = Array.from(shadow.querySelectorAll<HTMLButtonElement>('.provider'));
    providerButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const state = this.modalState;
        // The coin step reuses the picker's button, so read that first.
        const coin = btn.dataset.coin;
        if (coin && state?.kind === 'picking-coin') {
          this.chosenCoin = coin;
          this.chosenCoinLabel = state.coins.find((c) => c.id === coin)?.label ?? null;
          void this.pickProvider(state.provider);
          return;
        }
        const id = btn.dataset.providerId;
        if (!id || state?.kind !== 'picking') return;
        const provider = state.providers.find((p) => p.id === id);
        if (provider) void this.pickProvider(provider);
      });
    });

    // Quantity stepper: adjust in place (count, total, disabled states) so
    // the pressed button keeps focus — no full re-render between presses.
    const minusBtn = shadow.querySelector<HTMLButtonElement>('.qty-btn--minus');
    const plusBtn = shadow.querySelector<HTMLButtonElement>('.qty-btn--plus');
    if (minusBtn && plusBtn) {
      const step = (delta: number): void => {
        const max = this.maxQuantity ?? MAX_QUANTITY_FALLBACK;
        this.quantity = Math.min(max, Math.max(1, this.quantity + delta));
        minusBtn.disabled = this.quantity <= 1;
        plusBtn.disabled = this.quantity >= max;
        const value = shadow.querySelector<HTMLElement>('.qty-value');
        if (value) value.textContent = String(this.quantity);
        const total = shadow.querySelector<HTMLElement>('.qty-total');
        if (total) total.textContent = this.quantityTotal(this.getAttribute('amount') ?? '');
      };
      minusBtn.addEventListener('click', () => step(-1));
      plusBtn.addEventListener('click', () => step(1));
    }

    // Editable amount: keep the buyer's value live and gate the providers
    // on it being a positive number, so you can't check out for nothing.
    const amountInput = shadow.querySelector<HTMLInputElement>('.amount-input');
    if (amountInput) {
      const amountHint = shadow.querySelector<HTMLElement>('#cm-amount-error');
      const syncAmount = (): void => {
        const value = Number.parseFloat(amountInput.value);
        this.editableAmount = Number.isFinite(value) && value > 0 ? value : null;
        const invalid = this.editableAmount === null;
        providerButtons.forEach((b) => (b.disabled = invalid));
        // The prompt is guidance, not an error: only surface it once the buyer
        // has interacted and left the amount invalid. A fresh, untouched field
        // (or a valid value) shows nothing.
        const showHint = invalid && this.amountTouched;
        amountInput.setAttribute('aria-invalid', showHint ? 'true' : 'false');
        if (amountHint) amountHint.hidden = !showHint;
      };
      syncAmount();
      amountInput.addEventListener('input', () => {
        this.amountTouched = true;
        syncAmount();
      });
    }

    const confirmBtn = shadow.querySelector<HTMLButtonElement>(
      '.confirm-btn[data-confirm="manual"]',
    );
    confirmBtn?.addEventListener('click', () => {
      void this.confirmManualPayment();
    });

    // In-page crypto: copy the address, and the explicit "Check payment status".
    const copyBtn = shadow.querySelector<HTMLButtonElement>('.copy-btn');
    copyBtn?.addEventListener('click', () => {
      const text = copyBtn.dataset.copy ?? '';
      void navigator.clipboard?.writeText?.(text);
      copyBtn.textContent = 'Copied';
    });

    const checkBtn = shadow.querySelector<HTMLButtonElement>('[data-check-status]');
    checkBtn?.addEventListener('click', () => {
      void this.checkStatus(true);
    });

    // Crypto delivery email: validate, then continue checkout with the address.
    const collectEmailBtn = shadow.querySelector<HTMLButtonElement>(
      '[data-collect-email-continue]',
    );
    if (collectEmailBtn) {
      const emailInput = shadow.querySelector<HTMLInputElement>('.cm-email-input');
      const submitEmail = (): void => {
        const state = this.modalState;
        if (state?.kind !== 'collect-email') return;
        const email = (emailInput?.value ?? '').trim();
        // Optional (pay-by-mail): an empty field means "skip" — proceed with no email.
        if (state.optional && email === '') {
          void this.pickProvider(state.provider, undefined, true);
          return;
        }
        if (!isValidEmail(email)) {
          this.modalState = {
            ...state,
            error: state.optional
              ? 'Enter a valid email, or choose No thanks.'
              : 'Enter a valid email address so we can send your download.',
          };
          this.render();
          return;
        }
        void this.pickProvider(state.provider, email, true);
      };
      collectEmailBtn.addEventListener('click', submitEmail);
      emailInput?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          submitEmail();
        }
      });

      // Optional pay-by-mail: "No thanks" proceeds without an email.
      const skipBtn = shadow.querySelector<HTMLButtonElement>('[data-collect-email-skip]');
      skipBtn?.addEventListener('click', () => {
        const state = this.modalState;
        if (state?.kind !== 'collect-email') return;
        void this.pickProvider(state.provider, undefined, true);
      });
    }

    this.installFocusTrap(shadow);

    // Move focus into the dialog so keyboard users land where the action
    // is. Pick the first non-Cancel focusable — provider buttons in the
    // picking state, the close button otherwise.
    queueMicrotask(() => {
      const focusables = this.collectFocusables(shadow);
      const target =
        focusables.find((el) => !el.classList.contains('close')) ?? focusables[0] ?? null;
      target?.focus();
    });

    // Re-armed here because every render replaces the element it writes into.
    if (shadow.querySelector('[data-hold-until]') !== null) {
      this.startHoldCountdown();
    } else {
      this.stopHoldCountdown();
    }
  }
}

/** Element tag name. Exported so consumers can detect / re-use it. */
export const COIN_MOEBIUS_BUY_TAG = 'coin-moebius-buy';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(value: string): string {
  return escapeHtml(value);
}

/** Friendly client-side email gate. The worker re-validates strictly; this just
 *  catches obvious typos before handoff (one `@`, a dotted domain). */
function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/** Format a per-provider price for a picker button: `"20 USD"`, `"5 GBK"`. */
function formatProviderPrice(price: { amount: number; currency: string }): string {
  return `${price.amount} ${price.currency}`;
}

/**
 * The Goldback currency glyph — the same single-path SVG the marketing site's
 * goldback-symbol component and the Aquarian Metals app use. Inlined so the
 * button stays self-contained (no image fetch); `currentColor` and em sizing
 * keep it matching whatever text it sits in.
 */
const GOLDBACK_SVG = `<svg class="gb-symbol" viewBox="0 0 117 116" role="img" aria-label="Goldbacks"><path fill="currentColor" d="M116.378 54.2151C116.206 51.555 115.853 48.9112 115.323 46.318C114.97 44.5921 114.536 42.8793 114.025 41.19H63.0121L53.1986 54.2151H103.32C103.423 55.4678 103.476 56.7299 103.476 58.0002C103.476 61.4447 103.094 64.8296 102.335 68.1252H42.7183L32.9045 81.1503H97.0685C96.6628 81.8251 96.2391 82.4893 95.7985 83.1412C94.1886 85.5242 92.3397 87.7651 90.3029 89.8019C88.2661 91.8391 86.0252 93.688 83.6422 95.2979C81.2381 96.9218 78.6686 98.3168 76.0049 99.4431C70.4647 101.787 64.5754 102.975 58.5008 102.975C52.4266 102.975 46.5373 101.787 40.9967 99.4431C39.6082 98.856 38.2455 98.196 36.9182 97.4682C35.6994 96.8003 34.5105 96.0755 33.3594 95.2979C30.9765 93.688 28.7355 91.8391 26.6987 89.8019C26.6394 89.7424 26.5802 89.6828 26.5209 89.6232C24.5541 87.6342 22.7661 85.4545 21.2031 83.1412C19.5789 80.7375 18.1842 78.1679 17.0579 75.5043C14.7143 69.9637 13.5261 64.0744 13.5261 58.0002C13.5261 51.9256 14.7143 46.0363 17.0579 40.4957C18.1842 37.8324 19.5789 35.2629 21.2031 32.8588C22.813 30.4758 24.6619 28.2349 26.6987 26.1981C28.7355 24.1613 30.9768 22.3123 33.3594 20.7024C35.7635 19.0782 38.3331 17.6835 40.9967 16.5569C46.5373 14.2133 52.4266 13.0251 58.5008 13.0251C64.5754 13.0251 70.4647 14.2133 76.0049 16.5569C77.3934 17.1443 78.7562 17.8044 80.0834 18.5321L87.9851 8.04463C85.7614 6.73007 83.4494 5.56335 81.0792 4.56074C77.5758 3.07914 73.9099 1.94107 70.183 1.17845C66.3621 0.396612 62.4318 0 58.5008 0C54.5698 0 50.6395 0.396612 46.8186 1.17845C43.0918 1.94107 39.4259 3.07914 35.9228 4.56074C32.4845 6.015 29.1689 7.81442 26.068 9.90951C22.9983 11.9834 20.1116 14.3651 17.4886 16.988C14.8657 19.6109 12.4841 22.4973 10.4102 25.5673C8.31539 28.6683 6.51566 31.9838 5.0614 35.4218C3.5798 38.9249 2.44172 42.5911 1.6791 46.318C0.897279 50.1389 0.500977 54.0692 0.500977 58.0002C0.500977 61.9311 0.897279 65.8615 1.6791 69.6821C2.44172 73.4092 3.5798 77.0751 5.0614 80.5782C6.51566 84.0165 8.31539 87.3321 10.4102 90.433C12.4841 93.5027 14.8657 96.3894 17.4886 99.0123C17.8602 99.3839 18.237 99.7502 18.6186 100.112C20.9328 102.304 23.433 104.311 26.068 106.091C27.0312 106.741 28.0149 107.364 29.0169 107.956C31.2402 109.27 33.5522 110.437 35.9228 111.44C39.4259 112.921 43.0918 114.059 46.8186 114.822C50.6395 115.604 54.5698 116 58.5008 116C62.4318 116 66.3621 115.604 70.183 114.822C73.9099 114.059 77.5758 112.921 81.0792 111.44C84.5172 109.985 87.8327 108.186 90.9337 106.091C94.0034 104.017 96.89 101.635 99.513 99.0123C102.136 96.3894 104.518 93.5027 106.591 90.433C108.57 87.5043 110.285 84.3845 111.694 81.1503C111.777 80.9602 111.859 80.7694 111.94 80.5782C113.422 77.0751 114.56 73.4092 115.323 69.6821C115.428 69.165 115.527 68.6462 115.619 68.1252C116.204 64.7976 116.501 61.3991 116.501 58.0002C116.501 56.7371 116.46 55.4743 116.378 54.2151Z"/></svg>`;

/**
 * A currency for popup markup: Goldbacks get their glyph in place of the
 * "GBK" code; every other currency renders as its escaped code. Accessible
 * names ("Goldbacks", via the glyph's aria-label) stay intact.
 */
function currencyHtml(currency: string): string {
  return currency.trim().toUpperCase() === 'GBK' ? GOLDBACK_SVG : escapeHtml(currency);
}

/** formatProviderPrice as popup markup: the amount plus the display currency. */
function providerPriceHtml(price: { amount: number; currency: string }): string {
  return `${escapeHtml(String(price.amount))} ${currencyHtml(price.currency)}`;
}

/**
 * True when the providers carry more than one distinct price. Buttons only show
 * their own price when methods disagree; a single shared price is left to the
 * dialog header. Providers without a price are ignored for this check.
 */
function providerPricesVary(providers: PublicProviderInfo[]): boolean {
  const seen = new Set<string>();
  for (const p of providers) {
    if (p.price) seen.add(`${p.price.amount} ${p.price.currency}`);
  }
  return seen.size > 1;
}

/** The dialog title for a given modal state. */
function dialogTitle(state: ModalState): string {
  switch (state.kind) {
    case 'manual-preview':
      return 'Pay by mail';
    case 'manual-confirmed':
    case 'crypto-paid':
      return 'Payment confirmed';
    case 'crypto-pay':
      return receivedWithoutWaiting(state)
        ? 'Payment received'
        : `Pay with ${state.coinLabel ?? cryptoMethodName(state.provider)}`;
    case 'picking-coin':
      return `Pay with ${cryptoMethodName(state.provider)}`;
    case 'collect-email':
      return 'Where should we send your download?';
    default:
      return 'Choose payment method';
  }
}

/** Buyer-facing name for a method: the merchant's custom label, else the
 *  server-provided buyer-facing name. */
function cryptoMethodName(provider: PublicProviderInfo): string {
  if (provider.label !== undefined && provider.label !== '') return provider.label;
  return provider.name;
}

/**
 * Providers whose LIVE checkout renders in the page (address + QR) rather than
 * navigating to a hosted page. Only these show the crypto screen in preview;
 * everything else (cards, hosted crypto like NOWPayments) navigates, so in
 * preview it shows the navigate alert instead. Every self-hosted coin qualifies.
 */
const PREVIEW_IN_PAGE_CRYPTO = new Set<string>(['monero', 'zano', 'bitcoin', 'bitcoin-cash']);

/** The ticker shown beside the amount when a mint's reply names no coin. */
const COIN_TICKERS: Record<string, string> = {
  monero: 'XMR',
  bitcoin: 'BTC',
  'bitcoin-cash': 'BCH',
};

const RECEIVED_WITHOUT_WAITING = new Set<string>(['bitcoin-cash']);

function receivedWithoutWaiting(state: ModalState): boolean {
  return (
    state.kind === 'crypto-pay' &&
    RECEIVED_WITHOUT_WAITING.has(state.provider.id) &&
    (state.progress?.state === 'seen' || state.progress?.state === 'confirming') &&
    !shortOfExpected(state.progress)
  );
}

function reportedAmount(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

function shortOfExpected(progress: CryptoProgress | null | undefined): boolean {
  const received = reportedAmount(progress?.received);
  const expected = reportedAmount(progress?.expected);
  return received !== null && expected !== null && received < expected;
}

function receivedAmountLine(
  paid: CryptoInstructions | undefined,
  progress?: CryptoProgress | null,
): string {
  const amount = reportedAmount(progress?.received) ?? paid?.displayAmount ?? null;
  return paid && amount !== null
    ? `<p class="crypto-amount">${escapeHtml(`${amount} ${paid.coin}`)} received</p>`
    : '';
}

/**
 * Stand-in address and wallet-URI shape per in-page coin, so the preview looks
 * like that coin's real screen instead of always looking like Monero. Keyed by
 * provider id; a coin missing from here falls back to the Monero-shaped sample.
 */
const PREVIEW_CRYPTO_SAMPLES: Record<string, { address: string; uri: (a: string) => string }> = {
  monero: {
    address:
      '888tNkZrPN6JsEgekjMnABU4TBzc2Dt29EPAvkRxbANsAnjyPbb3iQ1YBRk1UXcdRsiKc9dhwMVgN5S9cQUiyaEABZ4Qf1y',
    uri: (a) => `monero:${a}`,
  },
  zano: {
    address:
      'iZ1qF9mKxYb7vQ3nR8sT2wC5dH6jL4pN9gA7eU3xV8yB2mS5kD4fW6hJ9rT3cX7nQ2bG8vL5sP4dK6yM3zR9tE2wA7uH',
    uri: (a) => `zano:action=send&address=${a}`,
  },
  bitcoin: {
    address: 'bc1qw508d6qejxtdg4y5r3zarvary0c5xw7kv8f3t4',
    uri: (a) => `bitcoin:${a}`,
  },
  'bitcoin-cash': {
    address: 'bitcoincash:qpazurdjn2gcwl0j8gpe7rd3n663gnhrmqgqmuw0ef',
    uri: (a) => a,
  },
};

/**
 * Stand-in crypto instructions for the preview: a realistic-looking address +
 * wallet URI + QR, so the merchant sees the exact screen a buyer would. The
 * amount is left unset (we don't have a live fiat→coin rate in preview); the
 * fiat amount still shows in the header.
 */
function samplePreviewCrypto(provider: PublicProviderInfo): CryptoInstructions {
  const sample = PREVIEW_CRYPTO_SAMPLES[provider.id] ?? PREVIEW_CRYPTO_SAMPLES['monero'];
  const address = sample?.address ?? '';
  return {
    paymentId: 'preview',
    address,
    uri: sample ? sample.uri(address) : address,
    displayAmount: null,
    coin: provider.name || 'crypto',
  };
}

/**
 * Recognize an in-page crypto checkout response (address + wallet URI +
 * paymentId) and normalize it. Returns null when the response isn't crypto
 * (e.g. a redirect `{ url }`), so the caller falls through to the redirect path.
 *
 * Coin-agnostic on purpose. Each self-hosted mint returns the SDK's own result
 * shape untouched, and those shapes name the amount differently: Monero says
 * `xmrAmount`, Zano says `assetAmount` (its rail carries several assets on one
 * chain), and a future rail may just say `amount`. We read whichever is there
 * rather than rewriting the mint's reply in the worker, which would cost us the
 * straight-through guarantee that the buyer sees exactly what the merchant's
 * own wallet minted. Same for the label: `coin`, then Zano's `ticker`, then a
 * per-provider default. A missing amount is not fatal, so it stays nullable and
 * the modal shows the address alone.
 */
/**
 * Each self-hosted rail's wallet-link scheme, and the link fields its own mint
 * writes. Anything else in a link is refused: a `lightning=` invoice or a
 * payjoin `pj=` endpoint rides along in a valid-looking Bitcoin link and sends
 * the money somewhere other than the address on screen.
 */
interface WalletLinkRule {
  schemes: ReadonlySet<string>;
  fields: ReadonlySet<string>;
  payeeCarriesScheme?: boolean;
}

const WALLET_LINKS: Readonly<Record<string, WalletLinkRule>> = {
  bitcoin: { schemes: new Set(['bitcoin']), fields: new Set(['amount', 'label', 'message']) },
  'bitcoin-cash': {
    schemes: new Set(['bitcoincash', 'bchtest', 'bchreg']),
    fields: new Set(['amount', 'label', 'message']),
    payeeCarriesScheme: true,
  },
  monero: {
    schemes: new Set(['monero']),
    fields: new Set(['tx_amount', 'tx_description', 'recipient_name']),
  },
  zano: {
    schemes: new Set(['zano']),
    fields: new Set(['action', 'address', 'amount', 'asset_id', 'comment']),
  },
};

/** The link field that carries the amount, per scheme. */
const AMOUNT_FIELD: Readonly<Record<string, string>> = {
  bitcoin: 'amount',
  'bitcoin-cash': 'amount',
  monero: 'tx_amount',
  zano: 'amount',
};

/**
 * The wallet link comes from the merchant's own server, by way of the worker,
 * and it lands in the "Open in wallet app" href and in the QR code. A server
 * that is compromised or misconfigured could send a `javascript:` link that
 * runs on the merchant's page, or a link that pays someone other than the
 * address printed, so the buyer who scans pays a stranger. So the link must:
 * use this rail's own scheme, pay exactly the address shown, carry only the
 * fields this rail's mint writes, and ask for the amount shown.
 */
function assertSafeWalletLink(
  uri: string,
  address: string,
  providerId: string,
  shownAmount: number | null,
): void {
  const link = WALLET_LINKS[providerId];
  const colon = uri.indexOf(':');
  const scheme = uri.slice(0, Math.max(0, colon));
  const parsed =
    link && link.schemes.has(scheme) ? readWalletLink(scheme, uri.slice(colon + 1)) : null;
  const amount = parsed?.fields.get(AMOUNT_FIELD[providerId] ?? '');
  const payee = parsed && link?.payeeCarriesScheme ? `${scheme}:${parsed.payee}` : parsed?.payee;
  const safe =
    parsed !== null &&
    address !== '' &&
    payee === address &&
    [...parsed.fields.keys()].every((key) => link!.fields.has(key)) &&
    (amount === undefined || shownAmount === null || Number(amount) === shownAmount);
  if (!safe) {
    throw new Error('coin-moebius-buy: checkout response carries an unsafe wallet link');
  }
}

/**
 * The address a wallet link pays and its fields, or null when either is
 * ambiguous (a field given twice). Bitcoin and Monero links put the payee
 * right after the scheme; Zano's documented `action=send` form names it in an
 * `address=` field.
 */
function readWalletLink(
  scheme: string,
  rest: string,
): { payee: string; fields: Map<string, string> } | null {
  const zanoForm = scheme === 'zano' && rest.startsWith('action=');
  const [head = '', query = ''] = zanoForm ? ['', rest] : rest.split(/\?(.*)/s);
  const fields = new Map<string, string>();
  for (const part of query === '' ? [] : query.split('&')) {
    const eq = part.indexOf('=');
    const key = eq === -1 ? part : part.slice(0, eq);
    if (fields.has(key)) return null;
    fields.set(key, eq === -1 ? '' : part.slice(eq + 1));
  }
  const payee = zanoForm ? fields.get('address') : head;
  return payee === undefined ? null : { payee, fields };
}

function parseCryptoInstructions(
  raw: Record<string, unknown>,
  provider: PublicProviderInfo,
): CryptoInstructions | null {
  const r = raw as Partial<CheckoutCryptoResponse>;
  if (
    typeof r.paymentId !== 'string' ||
    typeof r.address !== 'string' ||
    typeof r.uri !== 'string'
  ) {
    return null;
  }
  const firstFinite = (...values: unknown[]): number | null => {
    for (const v of values) {
      if (typeof v === 'number' && Number.isFinite(v)) return v;
    }
    return null;
  };
  const amount = firstFinite(r.xmrAmount, r.assetAmount, r.btcAmount, r.bchAmount, r.amount);
  assertSafeWalletLink(r.uri, r.address, provider.id, amount);
  const firstLabel = (...values: unknown[]): string | null => {
    for (const v of values) {
      if (typeof v === 'string' && v) return v;
    }
    return null;
  };
  const coin =
    firstLabel(r.coin, r.ticker) ?? COIN_TICKERS[provider.id] ?? cryptoMethodName(provider);
  return {
    paymentId: r.paymentId,
    address: r.address,
    uri: r.uri,
    displayAmount: amount,
    coin,
    ...(typeof r.expectedAmount === 'number' && Number.isFinite(r.expectedAmount)
      ? { expectedAmount: r.expectedAmount }
      : {}),
    ...(typeof r.expectedCurrency === 'string' && r.expectedCurrency
      ? { expectedCurrency: r.expectedCurrency }
      : {}),
    ...(typeof r.expiresAt === 'number' && Number.isFinite(r.expiresAt)
      ? { expiresAt: r.expiresAt }
      : {}),
    ...(typeof r.receiptUrl === 'string' && r.receiptUrl ? { receiptUrl: r.receiptUrl } : {}),
  };
}

/**
 * Equal amounts mean no rate was applied, so nothing can drift and no clock
 * is shown. Derived rather than listed, so pegged coins need no registry.
 */
function quoteFloats(p: CryptoInstructions): boolean {
  if (typeof p.expectedAmount !== 'number' || p.displayAmount === null) return false;
  return p.displayAmount !== p.expectedAmount;
}

/**
 * Scaled integers, not floats: 0.142 - 0.14 is 0.001999999999999974 in binary,
 * which no buyer can retype. Null when nothing is owed.
 */
function shortfall(expected: string | null, received: string | null): string | null {
  if (expected === null || received === null) return null;
  const want = Number(expected);
  const got = Number(received);
  if (!Number.isFinite(want) || !Number.isFinite(got) || want <= got) return null;
  const places = Math.max(decimalsOf(expected), decimalsOf(received));
  const scale = 10 ** places;
  const diff = Math.round(want * scale) - Math.round(got * scale);
  if (diff <= 0) return null;
  return places === 0
    ? String(diff)
    : (diff / scale).toFixed(places).replace(/0+$/, '').replace(/\.$/, '');
}

function decimalsOf(value: string): number {
  const dot = value.indexOf('.');
  return dot === -1 ? 0 : value.length - dot - 1;
}

function receiptBlock(url: string | undefined): string {
  if (!url) return '';
  return `<p class="crypto-receipt" part="crypto-receipt">Check this payment any time<br><a href="${escapeAttr(url)}">${escapeHtml(url)}</a></p>`;
}

function timeLeft(at: number, now: number): string | null {
  const ms = at - now;
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const total = Math.floor(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Render a scannable QR code (SVG) for a wallet URI. The QR is a convenience —
 * if generation ever fails, the address + Copy button still work, so we return
 * an empty string rather than throwing.
 */
function renderQrSvg(uri: string): string {
  try {
    const qr = qrcode(0, 'M');
    qr.addData(uri);
    qr.make();
    return qr.createSvgTag({ cellSize: 4, margin: 0, scalable: true });
  } catch {
    return '';
  }
}
